import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { SEOHead } from './components/SEOHead';
import { 
  Anchor,
  Ship,
  Wrench,
  Shield,
  Radio,
  Package,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Send,
  MessageCircle,
  Image,
  Plus,
  Trash2,
  LogIn,
  LogOut,
  Calendar,
  Instagram,
  Facebook,
  Youtube,
  Globe,
  Users,
  Briefcase,
  Award,
  Home,
  Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  es: {
    nav: {
      home: "Inicio",
      about: "Quiénes Somos",
      services: "Qué Hacemos",
      brands: "Marcas",
      works: "Trabajos",
      contact: "Contacto"
    },
    hero: {
      subtitle: "Productos y Servicios Náuticos",
      title: "Tu pasión por el mar,",
      highlight: "nuestra especialidad",
      description: "Más de 35 años equipando embarcaciones con los mejores productos y servicios náuticos del mercado.",
      cta1: "Ver Trabajos",
      cta2: "Contactar"
    },
    about: {
      subtitle: "Quiénes Somos",
      title: "DISTRIBUIDOR Y SERVICIO OFICIAL",
      title2: "NUESTROS",
      highlight: "SERVICIOS",
      description1: "Mextwave Marine es una empresa líder en el sector náutico, dedicada a proporcionar productos y servicios de la más alta calidad para embarcaciones de todo tipo.",
      description2: "Nuestro equipo de profesionales cuenta con amplia experiencia en el sector, garantizando soluciones efectivas y un servicio personalizado para cada cliente.",
      stats: {
        years: "Años de Experiencia",
        projects: "Proyectos Completados",
        clients: "Clientes Satisfechos",
        brands: "Marcas Asociadas"
      }
    },
    services: {
      subtitle: "Qué Hacemos",
      title: "NUESTROS",
      highlight: "SERVICIOS",
      description: "Ofrecemos soluciones integrales para tu embarcación.",
      items: [
        { title: "Venta de Embarcaciones", description: "Amplio catálogo de embarcaciones nuevas y de ocasión de las mejores marcas." },
        { title: "Mantenimiento Náutico", description: "Servicio técnico especializado para el mantenimiento y reparación de tu embarcación." },
        { title: "Accesorios y Recambios", description: "Todo lo que necesitas: cabos, defensas, anclas, pinturas y más." },
        { title: "Electrónica Marina", description: "Equipos de navegación, sondas, GPS, radares y sistemas de comunicación." },
        { title: "Equipamiento de Seguridad", description: "Chalecos, balsas, bengalas y todo el equipamiento homologado." },
        { title: "Invernaje y Varadero", description: "Servicios de almacenamiento, invernaje y puesta a punto de temporada." }
      ],
      more: "Más información"
    },
    brands: {
      subtitle: "Marcas",
      title: "TRABAJAMOS CON LAS",
      highlight: "MEJORES MARCAS",
      description: "Distribuimos productos de las marcas más reconocidas del sector náutico."
    },
    works: {
      subtitle: "Trabajos",
      title: "NUESTROS",
      highlight: "PROYECTOS",
      description: "Algunos de los trabajos que hemos realizado para nuestros clientes.",
      empty: "Aún no hay trabajos publicados",
      addWork: "Añadir Trabajo",
      viewAll: "Ver todos los trabajos",
      login: "Iniciar Sesión",
      logout: "Cerrar Sesión",
      delete: "Eliminar",
      edit: "Editar"
    },
    contact: {
      subtitle: "Contacto",
      title: "¿TIENES UN PROYECTO",
      highlight: "EN MENTE",
      description: "Contacta con nosotros y te asesoraremos sin compromiso.",
      form: {
        name: "Nombre completo",
        email: "Email",
        phone: "Teléfono",
        message: "Mensaje",
        send: "Enviar Mensaje",
        success: "¡Mensaje enviado correctamente!",
        error: "Error al enviar. Inténtalo de nuevo.",
        recipient: "Destinatario",
        generalTeam: "Equipo General",
        jaimeRole: "Ventas y Proyectos",
        miriamRole: "Operaciones",
        markusRole: "Técnico",
        locationLabel: "Ubicación",
        messageTo: "Mensaje para:"
      }
    },
    cookieBanner: {
      title: "Valoramos tu privacidad",
      text: "Usamos cookies para mejorar su experiencia de navegación, mostrarle anuncios o contenidos personalizados y analizar nuestro tráfico. Al hacer clic en \"Aceptar todo\" usted da su consentimiento a nuestro uso de las cookies.",
      acceptAll: "Aceptar todo",
      rejectAll: "Rechazar todo",
      customize: "Personalizar"
    },
    footer: {
      description: "Tu socio de confianza en productos y servicios náuticos.",
      slogan: "Tu pasión por el mar, nuestra especialidad.",
      services: "Servicios",
      company: "Compañía",
      contact: "Contacto",
      legal: "Aviso Legal",
      privacy: "Política de Privacidad",
      cookies: "Política de Cookies",
      rights: "Todos los derechos reservados."
    },
    legalModal: {
      title: "AVISO LEGAL",
      section1Title: "1. Datos Identificativos",
      company: "Empresa",
      address: "Dirección",
      phone: "Teléfono",
      email: "Correo electrónico",
      section2Title: "2. Objeto",
      section2Text: "El presente aviso legal regula el uso del sitio web www.nextwave-marine.com, del que es titular NextWave Marine SL. La navegación por el sitio web atribuye la condición de usuario e implica la aceptación plena y sin reservas de todas las disposiciones incluidas en este Aviso Legal.",
      section3Title: "3. Propiedad Intelectual",
      section3Text: "Todos los contenidos del sitio web (textos, fotografías, gráficos, imágenes, tecnología, software, diseño gráfico, etc.) son propiedad intelectual de NextWave Marine SL o de terceros, sin que puedan entenderse cedidos al usuario ninguno de los derechos de explotación reconocidos por la normativa vigente.",
      section4Title: "4. Condiciones de Uso",
      section4Text: "El usuario se compromete a hacer un uso adecuado de los contenidos y servicios que NextWave Marine SL ofrece a través de su sitio web, absteniéndose de emplearlos para actividades ilícitas o contrarias a la buena fe.",
      section5Title: "5. Protección de Datos",
      section5Text: "De conformidad con lo establecido en el Reglamento (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018 de Protección de Datos Personales, le informamos que los datos personales que nos proporcione serán tratados con la finalidad de gestionar su consulta y mantenerle informado de nuestros servicios.",
      section6Title: "6. Legislación Aplicable",
      section6Text: "Las presentes condiciones se rigen por la legislación española. Para cualquier controversia que pudiera derivarse del acceso o uso de este sitio web, las partes se someten a los Juzgados y Tribunales de Palma de Mallorca."
    },
    privacyModal: {
      title: "POLÍTICA DE PRIVACIDAD",
      section1Title: "1. Responsable del Tratamiento",
      section1Text: "NextWave Marine SL, con CIF B26952044 y domicilio en Son Puig 19, Palma (07011), es el responsable del tratamiento de sus datos personales.",
      section2Title: "2. Finalidad del Tratamiento",
      section2Text: "Los datos que nos facilite serán tratados con las siguientes finalidades: Gestionar las consultas realizadas a través del formulario de contacto. Enviar información comercial sobre nuestros productos y servicios, siempre que haya dado su consentimiento.",
      section3Title: "3. Legitimación",
      section3Text: "La base legal para el tratamiento de sus datos es: El consentimiento del interesado. La ejecución de un contrato o medidas precontractuales. El interés legítimo del responsable.",
      section4Title: "4. Destinatarios",
      section4Text: "No se cederán datos a terceros, salvo obligación legal o para la prestación del servicio contratado.",
      section5Title: "5. Derechos",
      section5Text: "Puede ejercer sus derechos de acceso, rectificación, supresión, limitación, portabilidad y oposición enviando un correo a info@nextwave-marine.com.",
      section6Title: "6. Conservación",
      section6Text: "Sus datos se conservarán mientras se mantenga la relación comercial y durante el tiempo necesario para cumplir con las obligaciones legales."
    },
    cookiesModal: {
      title: "POLÍTICA DE COOKIES",
      section1Title: "1. ¿Qué son las cookies?",
      section1Text: "Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita un sitio web. Permiten que el sitio recuerde sus acciones y preferencias durante un período de tiempo.",
      section2Title: "2. Tipos de cookies que utilizamos",
      technicalCookies: "Cookies técnicas",
      technicalCookiesText: "Necesarias para el funcionamiento básico del sitio web.",
      analyticsCookies: "Cookies analíticas",
      analyticsCookiesText: "Nos permiten analizar el uso del sitio para mejorar nuestros servicios.",
      section3Title: "3. Gestión de cookies",
      section3Text: "Puede configurar su navegador para rechazar cookies o para que le avise cuando se envían. Sin embargo, algunas funciones del sitio podrían no funcionar correctamente.",
      section4Title: "4. Más información",
      section4Text: "Para más información sobre el uso de cookies, puede contactarnos en info@nextwave-marine.com."
    },
    login: {
      title: "ACCESO ADMINISTRADOR",
      email: "Email",
      password: "Contraseña",
      submit: "Acceder",
      cancel: "Cancelar",
      error: "Credenciales incorrectas"
    },
    addWork: {
      title: "AÑADIR TRABAJO",
      editTitle: "EDITAR TRABAJO",
      name: "Título del trabajo",
      description: "Descripción",
      date: "Fecha",
      images: "Imágenes (máx. 5, formato cuadrado)",
      add: "Añadir",
      save: "Guardar Cambios",
      cancel: "Cancelar"
    },
    quienesSomos: {
      title: "QUIÉNES",
      highlight: "SOMOS",
      description1: "En Next Wave Marine somos distribuidores oficiales de Seakeeper y Dockmate, marcas líderes en estabilización giroscópica y sistemas de control remoto para maniobras de atraque.",
      description2: "Contamos con un equipo técnico de primer nivel, con la máxima cualificación y habilitación oficial en ambas firmas. Esta especialización nos permite desde el diseño técnico y la ejecución de instalaciones personalizadas, hasta la resolución experta de cualquier incidencia en sistemas instalados por los astilleros.",
      description3: "Nos mueve la pasión por la náutica; tratamos cada proyecto con la dedicación y el cuidado que pondríamos en nuestro propio barco. Para ofrecer un servicio llave en mano, realizamos todos los trabajos complementarios de hidráulica, mecánica, electricidad y fibra necesarios para que la integración sea impecable.",
      description4: "Estaremos encantados de visitar tu embarcación y diseñar un proyecto a tu medida.",
      description5: "¡Contacta con nosotros y solicita tu presupuesto sin compromiso!",
      button: "Conózcanos mejor"
    },
    teamPage: {
      title: "NUESTRO",
      titleHighlight: "EQUIPO",
      backHome: "Volver al inicio",
      member1Name: "Jaime Capell",
      member1Role: "Responsable de Ventas y Proyectos",
      member1Bio: "Ingeniero Mecánico y experto en estructuras e instalaciones industriales. Es también Capitán de Yate, un apasionado del mar y de los barcos. Lleva más de 40 años trabajando en la venta de proyectos y productos técnicos. Después de conocer el lanzamiento de Seakeeper en EE.UU., se fue a vivir a Mallorca y compró un barco en 2018 - un Beneteau Antares 9 – en el que montó uno de los primeros SK2, siendo el barco más pequeño de Europa con Seakeeper; posteriormente se unió al equipo del distribuidor, como responsable de ventas y proyectos, para ayudar a la consolidación de la empresa y a la expansión de Seakeeper, Dockmate y Opacmare en las Islas Baleares y el resto de España. En 2021 compró un Beneteau Swift Trawler 41 Fly – el actual barco demo de NextWave Marine – y lo equipó con toda la tecnología que la empresa recomienda a sus clientes (estabilizador Seakeeper 6, mando de control sin cables Dockmate, banco de Litio, potabilizadora, generador, etc) Aporta a NextWave Marine su experiencia, conocimiento técnico, capacidad de comunicación y entusiasmo contagioso.",
      member2Name: "Miriam Cirella",
      member2Role: "Coordinadora de Operaciones y Administración",
      member2Bio: "Miriam lleva muchos años navegando y desde hace unos cuantos, se dedica al mundo náutico a nivel profesional, donde ha descubierto una pasión que no deja de crecer. Lo que empezó como una oportunidad se ha convertido en una vocación que vive con entusiasmo día a día. Graduada en Economía, combina su afinidad por los números con una gran capacidad de organización y planificación. Le gusta tener todo bajo control y encontrar el equilibrio perfecto en cada detalle. En el día a día, es quien hace que todo fluya: agenda, tiempos y coordinación. Como un auténtico Seakeeper, aporta estabilidad al equipo y garantiza que todo funcione como un reloj suizo. Su objetivo es claro: cuidar cada detalle y asegurar una experiencia impecable y una satisfacción máxima de nuestros clientes.",
      member3Name: "Markus Ruesseler",
      member3Role: "Responsable Técnico",
      member3Bio: "Especialista en Sistemas Náuticos de Alta Gama. Con años de experiencia en el sector náutico, Markus Ruesseler es un técnico altamente cualificado especializado en la instalación y mantenimiento de sistemas avanzados para embarcaciones. Su trabajo se centra principalmente en soluciones de alto rendimiento como estabilizadores giroscópicos Seakeeper, mandos de control sin cables Dockmate y equipos premium de Opacmare. La pasión por la náutica y la precisión técnica definen cada uno de sus proyectos. Markus destaca por ofrecer instalaciones limpias, seguras y perfectamente integradas, siempre adaptadas a las exigencias específicas de cada cliente y embarcación. Cuenta con el título de Maestro de Seakeeper, la certificación más avanzada que avala su profundo conocimiento y experiencia en uno de los sistemas más innovadores del sector. Como técnico, Markus combina experiencia práctica, compromiso y una constante búsqueda de la excelencia, posicionándose como un profesional de la máxima confianza dentro de la industria náutica."
    },
    homepage: {
      watchVideo: "Ver video",
      products: "Productos",
      officialService: "Servicio Oficial",
      officialServiceDesc: "Comprometidos con los clientes",
      experience: "+35 Años",
      experienceDesc: "De experiencia en el sector",
      quality: "Calidad-Precio",
      qualityDesc: "Excelente relación coste-calidad",
      otherBrands: "OTRAS",
      otherBrandsHighlight: "MARCAS",
      visitOpacmare: "Visitar Opacmare",
      opacmareSubtitle: "Pasarelas y Plataformas",
      opacmareTopBrands: "Primeras marcas",
      otherServices: "OTROS",
      otherServicesHighlight: "SERVICIOS",
      viewServices: "Ver Servicios",
      comingSoon: "Próximamente",
      seaTrialTitle: "SOLICITE UNA PRUEBA DE MAR",
      seaTrialButton: "Solicitar",
      requestSeaTrial: "Solicitar Prueba de Mar"
    },
    otherBrands: {
      victronDesc: "Victron Energy es el líder mundial en sistemas de energía independiente para embarcaciones. Sus inversores, cargadores, controladores solares y sistemas de monitorización ofrecen la máxima fiabilidad y eficiencia energética a bordo.",
      victronFeatures: [
        "Inversores y cargadores de alta eficiencia",
        "Controladores de carga solar MPPT",
        "Monitorización remota con VRM Portal",
        "Sistemas de baterías de litio inteligentes",
        "Gestión integral de energía a bordo"
      ],
      ecosistemsDesc: "Eco-Sistems Watermakers diseña, desarrolla y fabrica potabilizadoras/desalinizadoras de agua de mar que se caracterizan por sus eficientes sistemas de ahorro de energía. De 25 a 300 litros/hora.",
      ecosistemsFeatures: [],
      fischerPandaDesc: "Fischer Panda es referente mundial en generadores marinos ultrasilenciosos y compactos. Sus sistemas de generación eléctrica están diseñados específicamente para el entorno náutico, ofreciendo máxima potencia con mínimo ruido y vibración.",
      fischerPandaFeatures: [
        "Generadores marinos ultrasilenciosos",
        "Diseño compacto y ligero",
        "Sistemas de propulsión híbridos",
        "Aire acondicionado marino",
        "Servicio técnico especializado"
      ],
      dometicDesc: "Dometic es líder global en soluciones de confort para embarcaciones. Desde sistemas de climatización marina hasta refrigeración, cocinas y equipamiento de cubierta, Dometic hace la vida a bordo más cómoda.",
      dometicFeatures: [
        "Aire acondicionado marino",
        "Refrigeración y congelación a bordo",
        "Cocinas y electrodomésticos marinos",
        "Toldos y equipamiento de cubierta",
        "Sistemas sanitarios marinos"
      ],
      servicesList: [
        "Aire Acondicionado",
        "Packs de baterías de Litio",
        "Generadores",
        "Potabilizadoras",
        "Etc."
      ],
      visitWebsite: "Visitar web",
      servicesSubtitle: "Productos y servicios complementarios para tu embarcación",
      noServices: "No hay servicios disponibles aún",
      addService: "Añadir Servicio",
      accessCode: "Código de acceso",
      enterCode: "Introduce el código",
      verify: "Verificar",
      newService: "Nuevo Servicio",
      photos: "Fotos (max 3)",
      photo: "Foto",
      productName: "Nombre del producto",
      productNamePlaceholder: "Ej: Aire Acondicionado Dometic",
      descLabel: "Descripción",
      descPlaceholder: "Descripción del producto o servicio...",
      createService: "Crear Servicio",
      wrongCode: "Código incorrecto"
    },
    seaTrial: {
      backHome: "Volver al inicio",
      title: "SOLICITE UNA",
      titleHighlight: "PRUEBA DE MAR",
      formName: "Nombre completo *",
      formNamePlaceholder: "Su nombre",
      formEmail: "Email *",
      formEmailPlaceholder: "correo@ejemplo.com",
      formPhone: "Teléfono",
      formPhonePlaceholder: "+34 600 000 000",
      formBoat: "Modelo de embarcación",
      formBoatPlaceholder: "Ej: Princess 62, Sunseeker 50...",
      formMessage: "Mensaje o comentarios",
      formMessagePlaceholder: "Cuéntenos más sobre su embarcación...",
      submitButton: "Solicitar Prueba",
      successMessage: "¡Solicitud enviada correctamente!",
      errorMessage: "Error al enviar. Inténtelo de nuevo.",
      infoTitle: "Prueba de Mar en Palma",
      infoDesc1: "Ven a comprobar la eficacia de Seakeeper en la bahía de Palma. Realizaremos pruebas en estático y dinámico (entre 3 y 10 nudos), buscando siempre el peor ángulo de oleaje —o usando la estela de los ferrys— para que veas cómo desaparece el balanceo.",
      infoDesc2: "Nuestras unidades de prueba usan baterías de litio, permitiéndote experimentar el confort de un fondeo silencioso, sin ruidos de motor ni generador. Este sistema de bajo consumo es aplicable a cualquier eslora desde barcos de 8 metros hasta grandes yates de más de 40 m. de eslora.",
      logisticsTitle: "Logística",
      logisticsDesc: "Palma está perfectamente conectada con vuelos diarios desde España y Europa. Nos adaptamos a tu agenda y organizamos la prueba cualquier día, incluyendo fines de semana.",
      location: "Bahía de Palma de Mallorca"
    },
    seakeeperPage: {
      subtitle: "Estabilizador giroscópico",
      advantagesTitle: "VENTAJAS DE",
      advantagesDesc: "Descubre por qué Seakeeper es la mejor inversión para tu embarcación",
      requestSeaTrial: "Solicitar Prueba de Mar",
      visitButton: "Visitar Seakeeper"
    },
    dockmatePage: {
      subtitle: "Control remoto de maniobras",
      advantagesTitle: "VENTAJAS DE",
      visitButton: "Visitar Dockmate",
      advantagesSubtitle: "Descubre por qué Dockmate revoluciona las maniobras de tu embarcación",
      watchVideo: "Ver video",
      contactButton: "Contactar"
    }
  },
  en: {
    nav: {
      home: "Home",
      about: "About Us",
      services: "Services",
      brands: "Brands",
      works: "Projects",
      contact: "Contact"
    },
    hero: {
      subtitle: "Marine Equipment & Services",
      title: "Your passion for the sea,",
      highlight: "our expertise",
      description: "Over 35 years outfitting vessels with premium marine equipment and professional nautical services.",
      cta1: "View Projects",
      cta2: "Get in Touch"
    },
    about: {
      subtitle: "About Us",
      title: "AUTHORIZED DEALER AND SERVICE CENTER",
      title2: "SPECIALISTS IN",
      highlight: "MARINE SYSTEMS",
      description1: "NextWave Marine is a premier marine equipment company dedicated to providing top-tier products and services for recreational and commercial vessels.",
      description2: "Our team of marine specialists brings extensive hands-on experience in vessel outfitting, ensuring tailored solutions and expert service for every boat owner.",
      stats: {
        years: "Years at Sea",
        projects: "Vessels Equipped",
        clients: "Satisfied Skippers",
        brands: "Premium Brands"
      }
    },
    services: {
      subtitle: "Services",
      title: "OUR",
      highlight: "SERVICES",
      description: "Comprehensive marine solutions for your vessel.",
      items: [
        { title: "Vessel Sales", description: "Extensive selection of new and pre-owned yachts and motorboats from leading shipyards." },
        { title: "Marine Engineering", description: "Expert technical service for hull maintenance, mechanical systems and vessel refits." },
        { title: "Chandlery & Spares", description: "Complete chandlery: mooring lines, fenders, ground tackle, antifouling and marine hardware." },
        { title: "Navigation Electronics", description: "Chart plotters, multifunction displays, radar systems, AIS and marine communication equipment." },
        { title: "Safety Equipment", description: "SOLAS-approved lifejackets, life rafts, EPIRBs, flares and man-overboard systems." },
        { title: "Winterization & Yard Services", description: "Haul-out, winter lay-up, hull maintenance and seasonal commissioning services." }
      ],
      more: "Learn more"
    },
    brands: {
      subtitle: "Brands",
      title: "PARTNERED WITH",
      highlight: "LEADING MARINE BRANDS",
      description: "Authorized distributor of the most trusted names in marine equipment."
    },
    works: {
      subtitle: "Projects",
      title: "OUR",
      highlight: "INSTALLATIONS",
      description: "A selection of vessel projects completed for our clients.",
      empty: "No projects published yet",
      addWork: "Add Project",
      viewAll: "View all projects",
      login: "Login",
      logout: "Logout",
      delete: "Delete",
      edit: "Edit"
    },
    contact: {
      subtitle: "Contact",
      title: "PLANNING YOUR NEXT",
      highlight: "REFIT",
      description: "Get in touch for a no-obligation consultation on your vessel project.",
      form: {
        name: "Full name",
        email: "Email",
        phone: "Phone",
        message: "Message",
        send: "Send Message",
        success: "Message sent successfully!",
        error: "Error sending. Please try again.",
        recipient: "Recipient",
        generalTeam: "General Team",
        jaimeRole: "Sales & Projects",
        miriamRole: "Operations",
        markusRole: "Technical",
        locationLabel: "Location",
        messageTo: "Message for:"
      }
    },
    cookieBanner: {
      title: "We value your privacy",
      text: "We use cookies to improve your browsing experience, show you personalised ads or content and analyse our traffic. By clicking \"Accept all\" you consent to our use of cookies.",
      acceptAll: "Accept all",
      rejectAll: "Reject all",
      customize: "Customise"
    },
    footer: {
      description: "Your trusted partner in nautical products and services since 1992.",
      slogan: "Your passion for the sea, our specialty.",
      services: "Services",
      company: "Company",
      contact: "Contact",
      legal: "Legal Notice",
      privacy: "Privacy Policy",
      cookies: "Cookie Policy",
      rights: "All rights reserved."
    },
    legalModal: {
      title: "LEGAL NOTICE",
      section1Title: "1. Identification Data",
      company: "Company",
      address: "Address",
      phone: "Phone",
      email: "Email",
      section2Title: "2. Purpose",
      section2Text: "This legal notice regulates the use of the website www.nextwave-marine.com, owned by NextWave Marine SL. Browsing the website grants the status of user and implies full and unreserved acceptance of all provisions included in this Legal Notice.",
      section3Title: "3. Intellectual Property",
      section3Text: "All website content (texts, photographs, graphics, images, technology, software, graphic design, etc.) is the intellectual property of NextWave Marine SL or third parties, and none of the exploitation rights recognized by current regulations may be understood as transferred to the user.",
      section4Title: "4. Terms of Use",
      section4Text: "The user agrees to make appropriate use of the content and services that NextWave Marine SL offers through its website, refraining from using them for illegal activities or activities contrary to good faith.",
      section5Title: "5. Data Protection",
      section5Text: "In accordance with Regulation (EU) 2016/679 (GDPR) and Organic Law 3/2018 on Personal Data Protection, we inform you that the personal data you provide will be processed for the purpose of managing your inquiry and keeping you informed of our services.",
      section6Title: "6. Applicable Law",
      section6Text: "These conditions are governed by Spanish law. For any dispute that may arise from access to or use of this website, the parties submit to the Courts and Tribunals of Palma de Mallorca."
    },
    privacyModal: {
      title: "PRIVACY POLICY",
      section1Title: "1. Data Controller",
      section1Text: "NextWave Marine SL, with CIF B26952044 and address at Son Puig 19, Palma (07011), is the controller responsible for your personal data.",
      section2Title: "2. Purpose of Processing",
      section2Text: "The data you provide will be processed for the following purposes: Managing inquiries made through the contact form. Sending commercial information about our products and services, provided you have given your consent.",
      section3Title: "3. Legal Basis",
      section3Text: "The legal basis for processing your data is: The consent of the data subject. The execution of a contract or pre-contractual measures. The legitimate interest of the controller.",
      section4Title: "4. Recipients",
      section4Text: "Data will not be transferred to third parties, except by legal obligation or for the provision of the contracted service.",
      section5Title: "5. Rights",
      section5Text: "You may exercise your rights of access, rectification, erasure, restriction, portability and objection by sending an email to info@nextwave-marine.com.",
      section6Title: "6. Retention",
      section6Text: "Your data will be kept as long as the commercial relationship is maintained and for the time necessary to comply with legal obligations."
    },
    cookiesModal: {
      title: "COOKIE POLICY",
      section1Title: "1. What are cookies?",
      section1Text: "Cookies are small text files that are stored on your device when you visit a website. They allow the site to remember your actions and preferences over a period of time.",
      section2Title: "2. Types of cookies we use",
      technicalCookies: "Technical cookies",
      technicalCookiesText: "Necessary for the basic functioning of the website.",
      analyticsCookies: "Analytics cookies",
      analyticsCookiesText: "Allow us to analyze the use of the site to improve our services.",
      section3Title: "3. Cookie management",
      section3Text: "You can configure your browser to reject cookies or to notify you when they are sent. However, some site features may not work properly.",
      section4Title: "4. More information",
      section4Text: "For more information about the use of cookies, you can contact us at info@nextwave-marine.com."
    },
    login: {
      title: "ADMIN ACCESS",
      email: "Email",
      password: "Password",
      submit: "Login",
      cancel: "Cancel",
      error: "Invalid credentials"
    },
    addWork: {
      title: "ADD WORK",
      editTitle: "EDIT WORK",
      name: "Work title",
      description: "Description",
      date: "Date",
      images: "Images (max. 5, square format)",
      add: "Add",
      save: "Save Changes",
      cancel: "Cancel"
    },
    quienesSomos: {
      title: "ABOUT",
      highlight: "US",
      description1: "At Next Wave Marine, we are official distributors of Seakeeper and Dockmate, leading brands in gyroscopic stabilization and remote control systems for docking maneuvers.",
      description2: "We have a top-level technical team, with the highest qualification and official authorization in both brands. This specialization allows us to handle everything from technical design and execution of custom installations, to expert resolution of any issues in systems installed by shipyards.",
      description3: "We are driven by our passion for nautical; we treat each project with the dedication and care we would put into our own boat. To offer a turnkey service, we carry out all complementary hydraulic, mechanical, electrical, and fiberglass work necessary for impeccable integration.",
      description4: "We will be happy to visit your boat and design a project tailored to your needs.",
      description5: "Contact us and request your no-obligation quote!",
      button: "Get to know us better"
    },
    teamPage: {
      title: "OUR",
      titleHighlight: "TEAM",
      backHome: "Back to home",
      member1Name: "Jaime Capell",
      member1Role: "Sales Manager",
      member1Bio: "Mechanical Engineer and expert in industrial structures and installations. He is also a Yacht Captain, passionate about the sea and boats. With over 40 years of experience selling technical projects and products. After learning about the Seakeeper launch in the USA, he moved to Mallorca and purchased a boat in 2018 - a Beneteau Antares 9 - on which he installed one of the first SK2 units, making it the smallest boat in Europe with a Seakeeper. He subsequently joined the distributor's team as Sales & Project Manager to help consolidate the company and expand Seakeeper, Dockmate and Opacmare in the Balearic Islands and throughout Spain. In 2021, he purchased a Beneteau Swift Trawler 41 Fly - now NextWave Marine's demo boat - and equipped it with all the technology the company recommends to its clients (Seakeeper 6 stabilizer, Dockmate wireless control, Lithium battery bank, watermaker, generator, etc.). He brings to NextWave Marine his experience, technical knowledge, communication skills and contagious enthusiasm.",
      member2Name: "Miriam Cirella",
      member2Role: "Coordinator & Administration",
      member2Bio: "Miriam has been sailing for many years and for several years now has been dedicated to the nautical world professionally, where she has discovered a passion that continues to grow. What started as an opportunity has become a vocation she lives with enthusiasm every day. With a degree in Economics, she combines her affinity for numbers with great organizational and planning skills. She likes to have everything under control and find the perfect balance in every detail. In day-to-day operations, she's the one who makes everything flow: schedules, timing, and coordination. Like a true Seakeeper, she brings stability to the team and ensures everything runs like a Swiss watch. Her goal is clear: take care of every detail and ensure an impeccable experience and maximum customer satisfaction.",
      member3Name: "Markus Ruesseler",
      member3Role: "Technical Manager",
      member3Bio: "Specialist in High-End Marine Systems. With years of experience in the marine sector, Markus Ruesseler is a highly qualified technician specialized in the installation and maintenance of advanced systems for vessels. His work focuses primarily on high-performance solutions such as Seakeeper gyroscopic stabilizers, Dockmate wireless controls and Opacmare premium equipment. Passion for boating and technical precision define each of his projects. Markus stands out for delivering clean, safe and perfectly integrated installations, always tailored to the specific requirements of each client and vessel. He holds the Seakeeper Master title, the most advanced certification that validates his deep knowledge and experience in one of the most innovative systems in the sector."
    },
    homepage: {
      watchVideo: "Watch video",
      products: "Products",
      officialService: "Official Service",
      officialServiceDesc: "Committed to customers",
      experience: "+35 Years",
      experienceDesc: "Of experience in the sector",
      quality: "Quality-Price",
      qualityDesc: "Excellent value for money",
      otherBrands: "OTHER",
      otherBrandsHighlight: "BRANDS",
      visitOpacmare: "Visit Opacmare",
      opacmareSubtitle: "Gangways and Platforms",
      opacmareTopBrands: "Leading brands",
      otherServices: "OTHER",
      otherServicesHighlight: "SERVICES",
      viewServices: "View Services",
      comingSoon: "Coming Soon",
      seaTrialTitle: "REQUEST A SEA TRIAL",
      seaTrialButton: "Request",
      requestSeaTrial: "Request Sea Trial"
    },
    otherBrands: {
      victronDesc: "Victron Energy is the world leader in independent power systems for vessels. Their inverters, chargers, solar controllers and monitoring systems offer maximum reliability and energy efficiency on board.",
      victronFeatures: [
        "High-efficiency inverters and chargers",
        "MPPT solar charge controllers",
        "Remote monitoring via VRM Portal",
        "Smart lithium battery systems",
        "Complete on-board energy management"
      ],
      ecosistemsDesc: "Eco-Sistems Watermakers designs, develops and manufactures seawater watermakers/desalinators characterised by their efficient energy-saving systems. From 25 to 300 litres/hour.",
      ecosistemsFeatures: [],
      fischerPandaDesc: "Fischer Panda is the world reference in ultra-quiet, compact marine generators. Their power generation systems are designed specifically for the marine environment, delivering maximum power with minimum noise and vibration.",
      fischerPandaFeatures: [
        "Ultra-quiet marine generators",
        "Compact and lightweight design",
        "Hybrid propulsion systems",
        "Marine air conditioning",
        "Specialist technical service"
      ],
      dometicDesc: "Dometic is the global leader in comfort solutions for vessels. From marine climate systems to refrigeration, galleys and deck equipment, Dometic makes life on board more comfortable.",
      dometicFeatures: [
        "Marine air conditioning",
        "On-board refrigeration and freezing",
        "Marine galleys and appliances",
        "Awnings and deck equipment",
        "Marine sanitation systems"
      ],
      servicesList: [
        "Air Conditioning",
        "Lithium Battery Packs",
        "Generators",
        "Watermakers",
        "Etc."
      ],
      visitWebsite: "Visit website",
      servicesSubtitle: "Complementary products and services for your vessel",
      noServices: "No services available yet",
      addService: "Add Service",
      accessCode: "Access code",
      enterCode: "Enter the code",
      verify: "Verify",
      newService: "New Service",
      photos: "Photos (max 3)",
      photo: "Photo",
      productName: "Product name",
      productNamePlaceholder: "E.g.: Dometic Air Conditioning",
      descLabel: "Description",
      descPlaceholder: "Product or service description...",
      createService: "Create Service",
      wrongCode: "Incorrect code"
    },
    seaTrial: {
      backHome: "Back to home",
      title: "REQUEST A",
      titleHighlight: "SEA TRIAL",
      formName: "Full name *",
      formNamePlaceholder: "Your name",
      formEmail: "Email *",
      formEmailPlaceholder: "email@example.com",
      formPhone: "Phone",
      formPhonePlaceholder: "+34 600 000 000",
      formBoat: "Boat model",
      formBoatPlaceholder: "Ex: Princess 62, Sunseeker 50...",
      formMessage: "Message or comments",
      formMessagePlaceholder: "Tell us more about your boat...",
      submitButton: "Request Trial",
      successMessage: "Request sent successfully!",
      errorMessage: "Error sending. Please try again.",
      infoTitle: "Sea Trial in Palma",
      infoDesc1: "Come and experience the effectiveness of Seakeeper in Palma Bay. We will conduct static and dynamic tests (between 3 and 10 knots), always seeking the worst wave angle —or using the ferry wake— so you can see how the rolling disappears.",
      infoDesc2: "Our test units use lithium batteries, allowing you to experience the comfort of a silent anchorage, without engine or generator noise. This low-consumption system is suitable for any hull length, from 8-metre boats to large yachts over 40 m. LOA.",
      logisticsTitle: "Logistics",
      logisticsDesc: "Palma is perfectly connected with daily flights from Spain and Europe. We adapt to your schedule and organize the trial any day, including weekends.",
      location: "Palma de Mallorca Bay"
    },
    seakeeperPage: {
      subtitle: "Gyroscopic stabilizer",
      advantagesTitle: "ADVANTAGES OF",
      advantagesDesc: "Discover why Seakeeper is the best investment for your vessel",
      requestSeaTrial: "Request Sea Trial",
      visitButton: "Visit Seakeeper",
      advantages: [
        { title: "Anchoring freedom", desc: "No need to depend on protected coves; you can drop anchor wherever you like without waves ruining the moment." },
        { title: "Goodbye seasickness", desc: "Eliminate the fear of guests prone to seasickness, allowing everyone to relax and enjoy the day." },
        { title: "Total effectiveness", desc: "Unlike fins, it works 100% even when the boat is stationary or navigating at low speed." },
        { title: "Unlimited navigation", desc: "Extend your days at sea; conditions that would have kept you in port are now perfectly navigable." },
        { title: "Safe transfer", desc: "Facilitate access from the tender by preventing the main boat from swinging abruptly." },
        { title: "Stable fishing platform", desc: "Essential for trolling or bottom fishing, allowing anglers to focus on the catch rather than keeping balance." },
        { title: "Less physical fatigue", desc: "Avoid extreme tiredness caused by constant muscle effort to compensate for boat rolling." },
        { title: "Safe loading maneuvers", desc: "Hoist or launch your jet ski or tender with total stability, reducing risk of bumps or accidents." },
        { title: "Profitable investment", desc: "A boat with Seakeeper has a much higher resale value and sells much faster than one without." },
        { title: "Comfort in port", desc: "Even when moored, it neutralizes the annoying roll caused by wakes from other boats or exposed ports." }
      ]
    },
    dockmatePage: {
      subtitle: "Wireless maneuvering control",
      advantagesTitle: "ADVANTAGES OF",
      visitButton: "Visit Dockmate",
      advantagesSubtitle: "Discover why Dockmate is revolutionising vessel maneuvering",
      watchVideo: "Watch video",
      contactButton: "Get in touch",
      advantages: [
        { title: "Total control", desc: "Dock and undock your vessel from any point on the boat with the wireless remote control." },
        { title: "Stress-free maneuvering", desc: "Eliminate the tension of port maneuvers, especially in tight spaces or windy conditions." },
        { title: "Increased safety", desc: "Avoid bumps and scratches by being able to control the vessel with total precision from bow or stern." },
        { title: "Solo navigation", desc: "Ideal for those who sail alone, without needing help when docking." },
        { title: "Compatible with your boat", desc: "Adapts to most propulsion systems: outboards, inboards, stern drives and pod drives." },
        { title: "Professional installation", desc: "Our certified technical team guarantees perfect integration with your vessel." },
        { title: "Ease of use", desc: "Intuitive interface that allows you to control your boat with maximum precision from the first moment." },
        { title: "Time saving", desc: "Significantly reduces the time needed for docking and undocking maneuvers." }
      ]
    }
  },
  de: {
    nav: {
      home: "Startseite",
      about: "Über Uns",
      services: "Leistungen",
      brands: "Marken",
      works: "Projekte",
      contact: "Kontakt"
    },
    hero: {
      subtitle: "Yachtausrüstung & Bordtechnik",
      title: "Ihre Leidenschaft für das Meer,",
      highlight: "unsere Expertise",
      description: "Über 35 Jahre Erfahrung in der Ausstattung von Yachten und Motorbooten mit hochwertiger Bordtechnik.",
      cta1: "Projekte ansehen",
      cta2: "Kontakt aufnehmen"
    },
    about: {
      subtitle: "Über Uns",
      title: "AUTORISIERTER HÄNDLER UND SERVICEPARTNER",
      title2: "SPEZIALISTEN FÜR",
      highlight: "YACHTTECHNIK",
      description1: "NextWave Marine ist ein führender Anbieter für Yachtausrüstung und bietet erstklassige Produkte und Dienstleistungen für Sport- und Freizeitboote.",
      description2: "Unser Team erfahrener Yachttechniker garantiert maßgeschneiderte Lösungen und fachkundige Betreuung für jeden Bootseigner.",
      stats: {
        years: "Jahre auf See",
        projects: "Ausgerüstete Yachten",
        clients: "Zufriedene Skipper",
        brands: "Premium-Marken"
      }
    },
    services: {
      subtitle: "Leistungen",
      title: "UNSERE",
      highlight: "LEISTUNGEN",
      description: "Umfassende Lösungen für Ihre Yacht.",
      items: [
        { title: "Yachtverkauf", description: "Große Auswahl an neuen und gebrauchten Yachten und Motorbooten führender Werften." },
        { title: "Yachttechnik", description: "Fachkundiger Service für Rumpfpflege, Antriebstechnik und Refit-Arbeiten." },
        { title: "Yachtzubehör & Ersatzteile", description: "Komplettes Sortiment: Festmacher, Fender, Ankergeschirr, Antifouling und Beschläge." },
        { title: "Navigationselektronik", description: "Kartenplotter, Multifunktionsdisplays, Radarsysteme, AIS und Bordfunk." },
        { title: "Sicherheitsausrüstung", description: "SOLAS-zugelassene Rettungswesten, Rettungsinseln, EPIRBs, Signalmittel und MOB-Systeme." },
        { title: "Winterlager & Werftservice", description: "Kranen, Winterlagerung, Unterwasserschiffpflege und saisonale Inbetriebnahme." }
      ],
      more: "Mehr erfahren"
    },
    brands: {
      subtitle: "Marken",
      title: "PARTNER VON",
      highlight: "FÜHRENDEN MARKEN",
      description: "Autorisierter Vertrieb der renommiertesten Marken im Yachtzubehör."
    },
    works: {
      subtitle: "Projekte",
      title: "UNSERE",
      highlight: "INSTALLATIONEN",
      description: "Eine Auswahl unserer realisierten Yachtprojekte.",
      empty: "Noch keine Projekte veröffentlicht",
      addWork: "Projekt hinzufügen",
      viewAll: "Alle Projekte anzeigen",
      login: "Anmelden",
      logout: "Abmelden",
      delete: "Löschen",
      edit: "Bearbeiten"
    },
    contact: {
      subtitle: "Kontakt",
      title: "PLANEN SIE IHR NÄCHSTES",
      highlight: "REFIT",
      description: "Kontaktieren Sie uns für eine unverbindliche Beratung zu Ihrem Yachtprojekt.",
      form: {
        name: "Vollständiger Name",
        email: "E-Mail",
        phone: "Telefon",
        message: "Nachricht",
        send: "Nachricht senden",
        success: "Nachricht erfolgreich gesendet!",
        error: "Fehler beim Senden. Bitte versuchen Sie es erneut.",
        recipient: "Empfänger",
        generalTeam: "Allgemeines Team",
        jaimeRole: "Vertrieb & Projekte",
        miriamRole: "Betrieb",
        markusRole: "Technik",
        locationLabel: "Standort",
        messageTo: "Nachricht an:"
      }
    },
    cookieBanner: {
      title: "Wir schätzen Ihre Privatsphäre",
      text: "Wir verwenden Cookies, um Ihr Surferlebnis zu verbessern, Ihnen personalisierte Werbung oder Inhalte anzuzeigen und unseren Datenverkehr zu analysieren. Durch Klicken auf \"Alle akzeptieren\" stimmen Sie unserer Verwendung von Cookies zu.",
      acceptAll: "Alle akzeptieren",
      rejectAll: "Alle ablehnen",
      customize: "Anpassen"
    },
    footer: {
      description: "Ihr vertrauenswürdiger Partner für nautische Produkte und Dienstleistungen seit 1992.",
      slogan: "Ihre Leidenschaft für das Meer, unsere Spezialität.",
      services: "Dienstleistungen",
      company: "Unternehmen",
      contact: "Kontakt",
      legal: "Impressum",
      privacy: "Datenschutz",
      cookies: "Cookie-Richtlinie",
      rights: "Alle Rechte vorbehalten."
    },
    legalModal: {
      title: "IMPRESSUM",
      section1Title: "1. Identifikationsdaten",
      company: "Unternehmen",
      address: "Adresse",
      phone: "Telefon",
      email: "E-Mail",
      section2Title: "2. Gegenstand",
      section2Text: "Dieses Impressum regelt die Nutzung der Website www.nextwave-marine.com, deren Eigentümer NextWave Marine SL ist. Das Surfen auf der Website verleiht den Status eines Nutzers und impliziert die vollständige und vorbehaltlose Annahme aller in diesem Impressum enthaltenen Bestimmungen.",
      section3Title: "3. Geistiges Eigentum",
      section3Text: "Alle Inhalte der Website (Texte, Fotos, Grafiken, Bilder, Technologie, Software, Grafikdesign usw.) sind geistiges Eigentum von NextWave Marine SL oder Dritten, und keines der nach geltendem Recht anerkannten Verwertungsrechte gilt als an den Nutzer übertragen.",
      section4Title: "4. Nutzungsbedingungen",
      section4Text: "Der Nutzer verpflichtet sich, die Inhalte und Dienste, die NextWave Marine SL über seine Website anbietet, angemessen zu nutzen und von deren Verwendung für rechtswidrige oder gegen Treu und Glauben verstoßende Aktivitäten abzusehen.",
      section5Title: "5. Datenschutz",
      section5Text: "Gemäß der Verordnung (EU) 2016/679 (DSGVO) und dem Organgesetz 3/2018 zum Schutz personenbezogener Daten informieren wir Sie, dass die von Ihnen bereitgestellten personenbezogenen Daten zum Zweck der Verwaltung Ihrer Anfrage und zur Information über unsere Dienstleistungen verarbeitet werden.",
      section6Title: "6. Anwendbares Recht",
      section6Text: "Diese Bedingungen unterliegen spanischem Recht. Für alle Streitigkeiten, die sich aus dem Zugang oder der Nutzung dieser Website ergeben können, unterwerfen sich die Parteien den Gerichten von Palma de Mallorca."
    },
    privacyModal: {
      title: "DATENSCHUTZERKLÄRUNG",
      section1Title: "1. Verantwortlicher",
      section1Text: "NextWave Marine SL, mit CIF B26952044 und Sitz in Son Puig 19, Palma (07011), ist der für Ihre personenbezogenen Daten Verantwortliche.",
      section2Title: "2. Zweck der Verarbeitung",
      section2Text: "Die von Ihnen bereitgestellten Daten werden für folgende Zwecke verarbeitet: Verwaltung von Anfragen über das Kontaktformular. Versand von kommerziellen Informationen über unsere Produkte und Dienstleistungen, sofern Sie Ihre Einwilligung erteilt haben.",
      section3Title: "3. Rechtsgrundlage",
      section3Text: "Die Rechtsgrundlage für die Verarbeitung Ihrer Daten ist: Die Einwilligung der betroffenen Person. Die Durchführung eines Vertrags oder vorvertraglicher Maßnahmen. Das berechtigte Interesse des Verantwortlichen.",
      section4Title: "4. Empfänger",
      section4Text: "Daten werden nicht an Dritte weitergegeben, außer aufgrund gesetzlicher Verpflichtung oder zur Erbringung der vertraglich vereinbarten Leistung.",
      section5Title: "5. Rechte",
      section5Text: "Sie können Ihre Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung, Übertragbarkeit und Widerspruch ausüben, indem Sie eine E-Mail an info@nextwave-marine.com senden.",
      section6Title: "6. Aufbewahrung",
      section6Text: "Ihre Daten werden aufbewahrt, solange die Geschäftsbeziehung besteht und für die zur Erfüllung gesetzlicher Verpflichtungen erforderliche Zeit."
    },
    cookiesModal: {
      title: "COOKIE-RICHTLINIE",
      section1Title: "1. Was sind Cookies?",
      section1Text: "Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden, wenn Sie eine Website besuchen. Sie ermöglichen es der Website, sich Ihre Aktionen und Einstellungen über einen bestimmten Zeitraum zu merken.",
      section2Title: "2. Arten von Cookies, die wir verwenden",
      technicalCookies: "Technische Cookies",
      technicalCookiesText: "Notwendig für die grundlegende Funktion der Website.",
      analyticsCookies: "Analyse-Cookies",
      analyticsCookiesText: "Ermöglichen uns die Analyse der Nutzung der Website zur Verbesserung unserer Dienste.",
      section3Title: "3. Cookie-Verwaltung",
      section3Text: "Sie können Ihren Browser so konfigurieren, dass Cookies abgelehnt werden oder Sie benachrichtigt werden, wenn sie gesendet werden. Einige Funktionen der Website funktionieren jedoch möglicherweise nicht ordnungsgemäß.",
      section4Title: "4. Weitere Informationen",
      section4Text: "Für weitere Informationen zur Verwendung von Cookies können Sie uns unter info@nextwave-marine.com kontaktieren."
    },
    login: {
      title: "ADMIN-ZUGANG",
      email: "E-Mail",
      password: "Passwort",
      submit: "Anmelden",
      cancel: "Abbrechen",
      error: "Ungültige Anmeldedaten"
    },
    addWork: {
      title: "ARBEIT HINZUFÜGEN",
      editTitle: "ARBEIT BEARBEITEN",
      name: "Arbeitstitel",
      description: "Beschreibung",
      date: "Datum",
      images: "Bilder (max. 5, quadratisches Format)",
      add: "Hinzufügen",
      save: "Änderungen Speichern",
      cancel: "Abbrechen"
    },
    quienesSomos: {
      title: "ÜBER",
      highlight: "UNS",
      description1: "Bei Next Wave Marine sind wir offizielle Distributoren von Seakeeper und Dockmate, führende Marken in gyroskopischer Stabilisierung und Fernsteuerungssystemen für Anlegemanöver.",
      description2: "Wir verfügen über ein erstklassiges technisches Team mit höchster Qualifikation und offizieller Zulassung beider Marken. Diese Spezialisierung ermöglicht uns alles von der technischen Planung und Durchführung kundenspezifischer Installationen bis hin zur fachkundigen Lösung von Problemen bei von Werften installierten Systemen.",
      description3: "Uns treibt die Leidenschaft für die Nautik an; wir behandeln jedes Projekt mit der Hingabe und Sorgfalt, die wir auch unserem eigenen Boot widmen würden. Um einen schlüsselfertigen Service zu bieten, führen wir alle ergänzenden Hydraulik-, Mechanik-, Elektro- und Glasfaserarbeiten durch, die für eine einwandfreie Integration erforderlich sind.",
      description4: "Wir freuen uns, Ihr Boot zu besuchen und ein auf Ihre Bedürfnisse zugeschnittenes Projekt zu entwerfen.",
      description5: "Kontaktieren Sie uns und fordern Sie Ihr unverbindliches Angebot an!",
      button: "Lernen Sie uns besser kennen"
    },
    teamPage: {
      title: "UNSER",
      titleHighlight: "TEAM",
      backHome: "Zurück zur Startseite",
      member1Name: "Jaime Capell",
      member1Role: "Vertriebsleiter",
      member1Bio: "Maschinenbauingenieur und Experte für industrielle Strukturen und Anlagen. Er ist auch Yachtkapitän und begeistert sich leidenschaftlich für das Meer und Boote. Mit über 40 Jahren Erfahrung im Verkauf technischer Projekte und Produkte. Nachdem er vom Seakeeper-Launch in den USA erfahren hatte, zog er nach Mallorca und kaufte 2018 ein Boot - eine Beneteau Antares 9 - auf der er eine der ersten SK2-Einheiten installierte und damit das kleinste Boot Europas mit Seakeeper wurde. Anschließend trat er dem Vertriebsteam als Sales & Project Manager bei, um das Unternehmen zu festigen und Seakeeper, Dockmate und Opacmare auf den Balearen und in ganz Spanien zu expandieren. 2021 kaufte er eine Beneteau Swift Trawler 41 Fly - jetzt das Demoboot von NextWave Marine - und stattete sie mit der gesamten Technologie aus, die das Unternehmen seinen Kunden empfiehlt (Seakeeper 6 Stabilisator, Dockmate Funkfernsteuerung, Lithium-Batteriebank, Wassermacher, Generator usw.). Er bringt bei NextWave Marine seine Erfahrung, sein technisches Wissen, seine Kommunikationsfähigkeiten und seinen ansteckenden Enthusiasmus ein.",
      member2Name: "Miriam Cirella",
      member2Role: "Koordinatorin & Verwaltung",
      member2Bio: "Miriam segelt seit vielen Jahren und widmet sich seit einigen Jahren beruflich der nautischen Welt, wo sie eine Leidenschaft entdeckt hat, die nicht aufhört zu wachsen. Was als Gelegenheit begann, ist zu einer Berufung geworden, die sie jeden Tag mit Begeisterung lebt. Mit einem Abschluss in Wirtschaftswissenschaften verbindet sie ihre Affinität zu Zahlen mit großartigen Organisations- und Planungsfähigkeiten. Sie hat gerne alles unter Kontrolle und findet die perfekte Balance in jedem Detail. Im Alltag ist sie diejenige, die alles zum Laufen bringt: Termine, Zeiten und Koordination. Wie ein echter Seakeeper bringt sie Stabilität ins Team und sorgt dafür, dass alles wie ein Schweizer Uhrwerk läuft. Ihr Ziel ist klar: auf jedes Detail achten und ein einwandfreies Erlebnis sowie maximale Kundenzufriedenheit gewährleisten.",
      member3Name: "Markus Ruesseler",
      member3Role: "Technischer Leiter",
      member3Bio: "Spezialist für hochwertige Yachtsysteme. Mit jahrelanger Erfahrung im Yachtsektor ist Markus Ruesseler ein hochqualifizierter Techniker, spezialisiert auf Installation und Wartung fortschrittlicher Systeme für Yachten. Seine Arbeit konzentriert sich hauptsächlich auf Hochleistungslösungen wie Seakeeper Kreiselstabilisatoren, Dockmate Funkfernsteuerungen und Opacmare Premium-Ausrüstung. Leidenschaft für Bootfahren und technische Präzision definieren jedes seiner Projekte. Markus zeichnet sich durch saubere, sichere und perfekt integrierte Installationen aus, immer auf die spezifischen Anforderungen jedes Kunden und jeder Yacht zugeschnitten. Er trägt den Titel Seakeeper Master, die fortschrittlichste Zertifizierung, die sein tiefes Wissen und seine Erfahrung in einem der innovativsten Systeme der Branche bestätigt."
    },
    homepage: {
      watchVideo: "Video ansehen",
      products: "Produkte",
      officialService: "Offizieller Service",
      officialServiceDesc: "Engagiert für Kunden",
      experience: "+35 Jahre",
      experienceDesc: "Erfahrung in der Branche",
      quality: "Preis-Leistung",
      qualityDesc: "Hervorragendes Preis-Leistungs-Verhältnis",
      otherBrands: "ANDERE",
      otherBrandsHighlight: "MARKEN",
      visitOpacmare: "Opacmare besuchen",
      opacmareSubtitle: "Gangways und Plattformen",
      opacmareTopBrands: "Erstklassige Marken",
      otherServices: "ANDERE",
      otherServicesHighlight: "DIENSTLEISTUNGEN",
      viewServices: "Dienste anzeigen",
      comingSoon: "Demnächst",
      seaTrialTitle: "PROBEFAHRT AUF SEE ANFRAGEN",
      seaTrialButton: "Anfragen",
      requestSeaTrial: "Probefahrt anfragen"
    },
    otherBrands: {
      victronDesc: "Victron Energy ist der Weltmarktführer für unabhängige Energiesysteme auf Yachten. Ihre Wechselrichter, Ladegeräte, Solarregler und Überwachungssysteme bieten höchste Zuverlässigkeit und Energieeffizienz an Bord.",
      victronFeatures: [
        "Hocheffiziente Wechselrichter und Ladegeräte",
        "MPPT-Solarladeregler",
        "Fernüberwachung über VRM Portal",
        "Intelligente Lithium-Batteriesysteme",
        "Komplettes Energiemanagement an Bord"
      ],
      ecosistemsDesc: "Eco-Sistems Watermakers entwirft, entwickelt und fertigt Seewasser-Entsalzungsanlagen, die sich durch ihre effizienten Energiesparsysteme auszeichnen. Von 25 bis 300 Liter/Stunde.",
      ecosistemsFeatures: [],
      fischerPandaDesc: "Fischer Panda ist die weltweite Referenz für ultraleise, kompakte Marinegeneratoren. Ihre Stromerzeugungssysteme sind speziell für die Yachtumgebung konzipiert und liefern maximale Leistung bei minimalem Geräusch und Vibration.",
      fischerPandaFeatures: [
        "Ultraleise Marinegeneratoren",
        "Kompaktes und leichtes Design",
        "Hybride Antriebssysteme",
        "Marine Klimaanlage",
        "Spezialisierter technischer Service"
      ],
      dometicDesc: "Dometic ist der globale Marktführer für Komfortlösungen auf Yachten. Von Klimaanlagen über Kühlung bis hin zu Kombüsen und Deckausrüstung – Dometic macht das Leben an Bord komfortabler.",
      dometicFeatures: [
        "Marine Klimaanlage",
        "Kühlung und Gefrieren an Bord",
        "Marine Kombüsen und Geräte",
        "Markisen und Deckausrüstung",
        "Marine Sanitärsysteme"
      ],
      servicesList: [
        "Klimaanlage",
        "Lithium-Batteriepacks",
        "Generatoren",
        "Wassermacher",
        "Usw."
      ],
      visitWebsite: "Webseite besuchen",
      servicesSubtitle: "Ergänzende Produkte und Dienstleistungen für Ihre Yacht",
      noServices: "Noch keine Dienstleistungen verfügbar",
      addService: "Dienst hinzufügen",
      accessCode: "Zugangscode",
      enterCode: "Code eingeben",
      verify: "Bestätigen",
      newService: "Neuer Dienst",
      photos: "Fotos (max 3)",
      photo: "Foto",
      productName: "Produktname",
      productNamePlaceholder: "Z.B.: Dometic Klimaanlage",
      descLabel: "Beschreibung",
      descPlaceholder: "Produkt- oder Dienstleistungsbeschreibung...",
      createService: "Dienst erstellen",
      wrongCode: "Falscher Code"
    },
    seaTrial: {
      backHome: "Zurück zur Startseite",
      title: "PROBEFAHRT",
      titleHighlight: "AUF SEE ANFRAGEN",
      formName: "Vollständiger Name *",
      formNamePlaceholder: "Ihr Name",
      formEmail: "E-Mail *",
      formEmailPlaceholder: "email@beispiel.com",
      formPhone: "Telefon",
      formPhonePlaceholder: "+34 600 000 000",
      formBoat: "Bootsmodell",
      formBoatPlaceholder: "Z.B.: Princess 62, Sunseeker 50...",
      formMessage: "Nachricht oder Kommentare",
      formMessagePlaceholder: "Erzählen Sie uns mehr über Ihr Boot...",
      submitButton: "Probefahrt anfragen",
      successMessage: "Anfrage erfolgreich gesendet!",
      errorMessage: "Fehler beim Senden. Bitte versuchen Sie es erneut.",
      infoTitle: "Probefahrt in Palma",
      infoDesc1: "Kommen Sie und erleben Sie die Wirksamkeit von Seakeeper in der Bucht von Palma. Wir führen statische und dynamische Tests durch (zwischen 3 und 10 Knoten), immer auf der Suche nach dem schlechtesten Wellenwinkel —oder mit dem Kielwasser der Fähren— damit Sie sehen können, wie das Rollen verschwindet.",
      infoDesc2: "Unsere Testeinheiten verwenden Lithiumbatterien, so dass Sie den Komfort eines ruhigen Ankerplatzes erleben können, ohne Motor- oder Generatorgeräusche. Dieses verbrauchsarme System ist für jede Rumpflänge geeignet, von 8-Meter-Booten bis zu großen Yachten über 40 m. Länge.",
      logisticsTitle: "Logistik",
      logisticsDesc: "Palma ist perfekt mit täglichen Flügen aus Spanien und Europa verbunden. Wir passen uns Ihrem Zeitplan an und organisieren die Probefahrt an jedem Tag, auch am Wochenende.",
      location: "Bucht von Palma de Mallorca"
    },
    seakeeperPage: {
      subtitle: "Kreiselstabilisator",
      advantagesTitle: "VORTEILE VON",
      advantagesDesc: "Entdecken Sie, warum Seakeeper die beste Investition für Ihre Yacht ist",
      requestSeaTrial: "Probefahrt anfragen",
      visitButton: "Seakeeper besuchen",
      advantages: [
        { title: "Ankerfreiheit", desc: "Keine Abhängigkeit von geschützten Buchten; ankern Sie wo es Ihnen gefällt, ohne dass Wellen den Moment verderben." },
        { title: "Auf Wiedersehen Seekrankheit", desc: "Beseitigen Sie die Angst seekranker Gäste und lassen Sie alle entspannen und den Tag genießen." },
        { title: "Totale Wirksamkeit", desc: "Im Gegensatz zu Flossen funktioniert es zu 100%, auch wenn das Boot steht oder bei niedriger Geschwindigkeit fährt." },
        { title: "Grenzenlose Navigation", desc: "Erweitern Sie Ihre Seetage; Bedingungen, die Sie früher im Hafen gehalten hätten, sind jetzt perfekt befahrbar." },
        { title: "Sicherer Überstieg", desc: "Erleichtern Sie den Zugang vom Beiboot, indem Sie verhindern, dass das Hauptboot abrupt schwankt." },
        { title: "Stabile Angelplattform", desc: "Unerlässlich für Schleppangeln oder Grundfischen, damit sich die Angler auf den Fang konzentrieren können." },
        { title: "Weniger körperliche Ermüdung", desc: "Vermeiden Sie extreme Müdigkeit durch ständige Muskelanspannung zum Ausgleich des Schaukelns." },
        { title: "Sichere Lademanöver", desc: "Hieven oder lassen Sie Ihren Jetski oder Ihr Beiboot mit voller Stabilität zu Wasser." },
        { title: "Rentable Investition", desc: "Eine Yacht mit Seakeeper hat einen viel höheren Wiederverkaufswert und verkauft sich schneller." },
        { title: "Komfort im Hafen", desc: "Selbst am Steg neutralisiert es das lästige Schaukeln durch Bugwellen anderer Boote." }
      ]
    },
    dockmatePage: {
      subtitle: "Kabellose Manöversteuerung",
      advantagesTitle: "VORTEILE VON",
      visitButton: "Dockmate besuchen",
      advantagesSubtitle: "Entdecken Sie, warum Dockmate das Manövrieren Ihrer Yacht revolutioniert",
      watchVideo: "Video ansehen",
      contactButton: "Kontakt aufnehmen",
      advantages: [
        { title: "Totale Kontrolle", desc: "Anlegen und Ablegen von jedem Punkt des Bootes mit der kabellosen Fernbedienung." },
        { title: "Stressfreies Manövrieren", desc: "Beseitigen Sie die Anspannung bei Hafenmanövern, besonders bei beengten Verhältnissen oder Wind." },
        { title: "Erhöhte Sicherheit", desc: "Vermeiden Sie Stöße und Kratzer durch präzise Kontrolle vom Bug oder Heck aus." },
        { title: "Solo-Navigation", desc: "Ideal für Alleinsegler ohne Hilfe beim Anlegen." },
        { title: "Kompatibel mit Ihrer Yacht", desc: "Passt zu den meisten Antriebssystemen: Außenborder, Innenborder, Z-Antriebe und Pod-Antriebe." },
        { title: "Professionelle Installation", desc: "Unser zertifiziertes Technikteam garantiert perfekte Integration mit Ihrer Yacht." },
        { title: "Einfache Bedienung", desc: "Intuitive Oberfläche für maximale Präzision von der ersten Sekunde an." },
        { title: "Zeitersparnis", desc: "Reduziert die Zeit für An- und Ablegemanöver erheblich." }
      ]
    }
  },
  it: {
    nav: {
      home: "Home",
      about: "Chi Siamo",
      services: "Servizi",
      brands: "Marchi",
      works: "Progetti",
      contact: "Contatti"
    },
    hero: {
      subtitle: "Accessori Nautici & Impiantistica di Bordo",
      title: "La tua passione per il mare,",
      highlight: "la nostra competenza",
      description: "Oltre 35 anni di esperienza nell'allestimento di yacht e imbarcazioni da diporto con le migliori attrezzature nautiche.",
      cta1: "Vedi Progetti",
      cta2: "Contattaci"
    },
    about: {
      subtitle: "Chi Siamo",
      title: "DISTRIBUTORE AUTORIZZATO E CENTRO ASSISTENZA",
      title2: "SPECIALISTI IN",
      highlight: "IMPIANTISTICA NAUTICA",
      description1: "NextWave Marine è un'azienda leader nel settore della nautica da diporto, dedicata a fornire prodotti e servizi di prima qualità per yacht e imbarcazioni.",
      description2: "Il nostro team di tecnici nautici esperti garantisce soluzioni su misura e assistenza professionale per ogni armatore.",
      stats: {
        years: "Anni in Mare",
        projects: "Imbarcazioni Allestite",
        clients: "Armatori Soddisfatti",
        brands: "Marchi Premium"
      }
    },
    services: {
      subtitle: "Servizi",
      title: "I NOSTRI",
      highlight: "SERVIZI",
      description: "Soluzioni complete per la tua imbarcazione.",
      items: [
        { title: "Vendita Imbarcazioni", description: "Ampia selezione di yacht e motoscafi nuovi e usati dei migliori cantieri." },
        { title: "Impiantistica Nautica", description: "Servizio tecnico specializzato per carene, motoristica e refit completi." },
        { title: "Accessori & Ricambi", description: "Forniture complete: cime d'ormeggio, parabordi, ancore, antivegetativa e ferramenta nautica." },
        { title: "Elettronica di Navigazione", description: "Chartplotter, display multifunzione, sistemi radar, AIS e apparati radio VHF." },
        { title: "Dotazioni di Sicurezza", description: "Giubbotti SOLAS, zattere autogonfiabili, EPIRB, segnali di soccorso e sistemi MOB." },
        { title: "Rimessaggio & Cantiere", description: "Alaggio, rimessaggio invernale, manutenzione carena e commissioning stagionale." }
      ],
      more: "Scopri di più"
    },
    brands: {
      subtitle: "Marchi",
      title: "PARTNER DEI",
      highlight: "MIGLIORI MARCHI NAUTICI",
      description: "Distributore autorizzato dei marchi più prestigiosi nel settore degli accessori nautici."
    },
    works: {
      subtitle: "Progetti",
      title: "LE NOSTRE",
      highlight: "INSTALLAZIONI",
      description: "Una selezione dei progetti realizzati per i nostri clienti.",
      empty: "Nessun progetto pubblicato ancora",
      addWork: "Aggiungi Progetto",
      viewAll: "Vedi tutti i progetti",
      login: "Accedi",
      logout: "Esci",
      delete: "Elimina",
      edit: "Modifica"
    },
    contact: {
      subtitle: "Contatti",
      title: "STAI PIANIFICANDO IL TUO PROSSIMO",
      highlight: "REFIT",
      description: "Contattaci per una consulenza senza impegno sul tuo progetto nautico.",
      form: {
        name: "Nome completo",
        email: "Email",
        phone: "Telefono",
        message: "Messaggio",
        send: "Invia Messaggio",
        success: "Messaggio inviato con successo!",
        error: "Errore nell'invio. Riprova.",
        recipient: "Destinatario",
        generalTeam: "Team Generale",
        jaimeRole: "Vendite e Progetti",
        miriamRole: "Operazioni",
        markusRole: "Tecnico",
        locationLabel: "Posizione",
        messageTo: "Messaggio per:"
      }
    },
    cookieBanner: {
      title: "Apprezziamo la tua privacy",
      text: "Utilizziamo i cookie per migliorare la tua esperienza di navigazione, mostrarti annunci o contenuti personalizzati e analizzare il nostro traffico. Cliccando su \"Accetta tutto\" acconsenti all'uso dei cookie.",
      acceptAll: "Accetta tutto",
      rejectAll: "Rifiuta tutto",
      customize: "Personalizza"
    },
    footer: {
      description: "Il tuo partner di fiducia in prodotti e servizi nautici dal 1992.",
      slogan: "La tua passione per il mare, la nostra specialità.",
      services: "Servizi",
      company: "Azienda",
      contact: "Contatto",
      legal: "Note Legali",
      privacy: "Informativa sulla Privacy",
      cookies: "Politica dei Cookie",
      rights: "Tutti i diritti riservati."
    },
    legalModal: {
      title: "NOTE LEGALI",
      section1Title: "1. Dati Identificativi",
      company: "Azienda",
      address: "Indirizzo",
      phone: "Telefono",
      email: "Email",
      section2Title: "2. Oggetto",
      section2Text: "Il presente avviso legale regola l'uso del sito web www.nextwave-marine.com, di proprietà di NextWave Marine SL. La navigazione nel sito web attribuisce la condizione di utente e implica l'accettazione piena e senza riserve di tutte le disposizioni incluse in questo Avviso Legale.",
      section3Title: "3. Proprietà Intellettuale",
      section3Text: "Tutti i contenuti del sito web (testi, fotografie, grafica, immagini, tecnologia, software, design grafico, ecc.) sono proprietà intellettuale di NextWave Marine SL o di terzi, senza che possano intendersi ceduti all'utente alcuno dei diritti di sfruttamento riconosciuti dalla normativa vigente.",
      section4Title: "4. Condizioni d'Uso",
      section4Text: "L'utente si impegna a fare un uso adeguato dei contenuti e dei servizi che NextWave Marine SL offre attraverso il suo sito web, astenendosi dall'utilizzarli per attività illecite o contrarie alla buona fede.",
      section5Title: "5. Protezione dei Dati",
      section5Text: "In conformità con il Regolamento (UE) 2016/679 (GDPR) e la Legge Organica 3/2018 sulla Protezione dei Dati Personali, La informiamo che i dati personali che ci fornirà saranno trattati al fine di gestire la Sua richiesta e tenerLa informato sui nostri servizi.",
      section6Title: "6. Legislazione Applicabile",
      section6Text: "Le presenti condizioni sono regolate dalla legislazione spagnola. Per qualsiasi controversia che possa derivare dall'accesso o dall'uso di questo sito web, le parti si sottopongono ai Tribunali di Palma di Maiorca."
    },
    privacyModal: {
      title: "INFORMATIVA SULLA PRIVACY",
      section1Title: "1. Titolare del Trattamento",
      section1Text: "NextWave Marine SL, con CIF B26952044 e sede in Son Puig 19, Palma (07011), è il titolare del trattamento dei Suoi dati personali.",
      section2Title: "2. Finalità del Trattamento",
      section2Text: "I dati che ci fornirà saranno trattati per le seguenti finalità: Gestire le richieste effettuate tramite il modulo di contatto. Inviare informazioni commerciali sui nostri prodotti e servizi, sempre che abbia dato il Suo consenso.",
      section3Title: "3. Base Giuridica",
      section3Text: "La base giuridica per il trattamento dei Suoi dati è: Il consenso dell'interessato. L'esecuzione di un contratto o di misure precontrattuali. L'interesse legittimo del titolare.",
      section4Title: "4. Destinatari",
      section4Text: "I dati non saranno ceduti a terzi, salvo obbligo legale o per la prestazione del servizio contrattato.",
      section5Title: "5. Diritti",
      section5Text: "Può esercitare i Suoi diritti di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione inviando un'email a info@nextwave-marine.com.",
      section6Title: "6. Conservazione",
      section6Text: "I Suoi dati saranno conservati finché si mantiene il rapporto commerciale e per il tempo necessario ad adempiere agli obblighi legali."
    },
    cookiesModal: {
      title: "POLITICA DEI COOKIE",
      section1Title: "1. Cosa sono i cookie?",
      section1Text: "I cookie sono piccoli file di testo che vengono memorizzati sul Suo dispositivo quando visita un sito web. Permettono al sito di ricordare le Sue azioni e preferenze per un periodo di tempo.",
      section2Title: "2. Tipi di cookie che utilizziamo",
      technicalCookies: "Cookie tecnici",
      technicalCookiesText: "Necessari per il funzionamento di base del sito web.",
      analyticsCookies: "Cookie analitici",
      analyticsCookiesText: "Ci permettono di analizzare l'uso del sito per migliorare i nostri servizi.",
      section3Title: "3. Gestione dei cookie",
      section3Text: "Può configurare il Suo browser per rifiutare i cookie o per essere avvisato quando vengono inviati. Tuttavia, alcune funzioni del sito potrebbero non funzionare correttamente.",
      section4Title: "4. Maggiori informazioni",
      section4Text: "Per maggiori informazioni sull'uso dei cookie, può contattarci a info@nextwave-marine.com."
    },
    login: {
      title: "ACCESSO ADMIN",
      email: "Email",
      password: "Password",
      submit: "Accedi",
      cancel: "Annulla",
      error: "Credenziali non valide"
    },
    addWork: {
      title: "AGGIUNGI LAVORO",
      editTitle: "MODIFICA LAVORO",
      name: "Titolo del lavoro",
      description: "Descrizione",
      date: "Data",
      images: "Immagini (max. 5, formato quadrato)",
      add: "Aggiungi",
      save: "Salva Modifiche",
      cancel: "Annulla"
    },
    quienesSomos: {
      title: "CHI",
      highlight: "SIAMO",
      description1: "In Next Wave Marine siamo distributori ufficiali di Seakeeper e Dockmate, marchi leader nella stabilizzazione giroscopica e nei sistemi di controllo remoto per le manovre di ormeggio.",
      description2: "Abbiamo un team tecnico di primo livello, con la massima qualificazione e abilitazione ufficiale in entrambi i marchi. Questa specializzazione ci permette di occuparci dalla progettazione tecnica e l'esecuzione di installazioni personalizzate, alla risoluzione esperta di qualsiasi problema nei sistemi installati dai cantieri navali.",
      description3: "Ci muove la passione per la nautica; trattiamo ogni progetto con la dedizione e la cura che metteremmo nella nostra barca. Per offrire un servizio chiavi in mano, realizziamo tutti i lavori complementari di idraulica, meccanica, elettricità e fibra necessari per un'integrazione impeccabile.",
      description4: "Saremo lieti di visitare la tua imbarcazione e progettare un progetto su misura per te.",
      description5: "Contattaci e richiedi il tuo preventivo senza impegno!",
      button: "Conoscici meglio"
    },
    teamPage: {
      title: "IL NOSTRO",
      titleHighlight: "TEAM",
      backHome: "Torna alla home",
      member1Name: "Jaime Capell",
      member1Role: "Responsabile Vendite",
      member1Bio: "Ingegnere Meccanico ed esperto in strutture e impianti industriali. È anche Capitano di Yacht, appassionato del mare e delle barche. Con oltre 40 anni di esperienza nella vendita di progetti e prodotti tecnici. Dopo aver conosciuto il lancio di Seakeeper negli USA, si è trasferito a Maiorca e nel 2018 ha acquistato una barca - una Beneteau Antares 9 - sulla quale ha installato una delle prime unità SK2, diventando la barca più piccola d'Europa con Seakeeper. Successivamente è entrato nel team del distributore come Sales & Project Manager per aiutare a consolidare l'azienda e ad espandere Seakeeper, Dockmate e Opacmare nelle Isole Baleari e in tutta la Spagna. Nel 2021 ha acquistato una Beneteau Swift Trawler 41 Fly - ora la barca demo di NextWave Marine - e l'ha equipaggiata con tutta la tecnologia che l'azienda raccomanda ai suoi clienti (stabilizzatore Seakeeper 6, telecomando wireless Dockmate, banco batterie al Litio, dissalatore, generatore, ecc.). Porta a NextWave Marine la sua esperienza, conoscenza tecnica, capacità comunicative e un entusiasmo contagioso.",
      member2Name: "Miriam Cirella",
      member2Role: "Coordinatrice e Amministrazione",
      member2Bio: "Miriam naviga da molti anni e da alcuni anni si dedica al mondo nautico a livello professionale, dove ha scoperto una passione che non smette di crescere. Quello che è iniziato come un'opportunità è diventato una vocazione che vive con entusiasmo ogni giorno. Laureata in Economia, combina la sua affinità per i numeri con grandi capacità organizzative e di pianificazione. Le piace avere tutto sotto controllo e trovare l'equilibrio perfetto in ogni dettaglio. Nel quotidiano, è lei che fa fluire tutto: agenda, tempi e coordinamento. Come un autentico Seakeeper, apporta stabilità al team e garantisce che tutto funzioni come un orologio svizzero. Il suo obiettivo è chiaro: curare ogni dettaglio e assicurare un'esperienza impeccabile e la massima soddisfazione dei nostri clienti.",
      member3Name: "Markus Ruesseler",
      member3Role: "Responsabile Tecnico",
      member3Bio: "Specialista in Sistemi Nautici di Alta Gamma. Con anni di esperienza nel settore nautico, Markus Ruesseler è un tecnico altamente qualificato specializzato nell'installazione e manutenzione di sistemi avanzati per imbarcazioni. Il suo lavoro si concentra principalmente su soluzioni ad alte prestazioni come stabilizzatori giroscopici Seakeeper, telecomandi wireless Dockmate e attrezzature premium Opacmare. La passione per la nautica e la precisione tecnica definiscono ogni suo progetto. Markus si distingue per installazioni pulite, sicure e perfettamente integrate, sempre adattate alle esigenze specifiche di ogni cliente e imbarcazione. Detiene il titolo di Maestro Seakeeper, la certificazione più avanzata che convalida la sua profonda conoscenza ed esperienza in uno dei sistemi più innovativi del settore."
    },
    homepage: {
      watchVideo: "Guarda video",
      products: "Prodotti",
      officialService: "Servizio Ufficiale",
      officialServiceDesc: "Impegnati con i clienti",
      experience: "+35 Anni",
      experienceDesc: "Di esperienza nel settore",
      quality: "Qualità-Prezzo",
      qualityDesc: "Eccellente rapporto qualità-prezzo",
      otherBrands: "ALTRI",
      otherBrandsHighlight: "MARCHI",
      visitOpacmare: "Visita Opacmare",
      opacmareSubtitle: "Passerelle e Piattaforme",
      opacmareTopBrands: "Marchi di prima classe",
      otherServices: "ALTRI",
      otherServicesHighlight: "SERVIZI",
      viewServices: "Vedi Servizi",
      comingSoon: "Prossimamente",
      seaTrialTitle: "RICHIEDI UNA PROVA IN MARE",
      seaTrialButton: "Richiedi",
      requestSeaTrial: "Richiedi Prova in Mare"
    },
    otherBrands: {
      victronDesc: "Victron Energy è il leader mondiale nei sistemi di energia indipendente per imbarcazioni. I loro inverter, caricabatterie, regolatori solari e sistemi di monitoraggio offrono la massima affidabilità ed efficienza energetica a bordo.",
      victronFeatures: [
        "Inverter e caricabatterie ad alta efficienza",
        "Regolatori di carica solare MPPT",
        "Monitoraggio remoto tramite VRM Portal",
        "Sistemi intelligenti di batterie al litio",
        "Gestione completa dell'energia a bordo"
      ],
      ecosistemsDesc: "Eco-Sistems Watermakers progetta, sviluppa e produce potabilizzatori/desalinizzatori di acqua di mare caratterizzati da efficienti sistemi di risparmio energetico. Da 25 a 300 litri/ora.",
      ecosistemsFeatures: [],
      fischerPandaDesc: "Fischer Panda è il riferimento mondiale per generatori marini ultra-silenziosi e compatti. I loro sistemi di generazione elettrica sono progettati specificamente per l'ambiente nautico, offrendo massima potenza con minimo rumore e vibrazione.",
      fischerPandaFeatures: [
        "Generatori marini ultra-silenziosi",
        "Design compatto e leggero",
        "Sistemi di propulsione ibridi",
        "Aria condizionata marina",
        "Servizio tecnico specializzato"
      ],
      dometicDesc: "Dometic è il leader globale nelle soluzioni di comfort per imbarcazioni. Dai sistemi di climatizzazione marina alla refrigerazione, cucine e attrezzature di coperta, Dometic rende la vita a bordo più confortevole.",
      dometicFeatures: [
        "Aria condizionata marina",
        "Refrigerazione e congelamento a bordo",
        "Cucine ed elettrodomestici marini",
        "Tendalini e attrezzature di coperta",
        "Sistemi sanitari marini"
      ],
      servicesList: [
        "Aria Condizionata",
        "Pacchi batterie al Litio",
        "Generatori",
        "Potabilizzatori",
        "Ecc."
      ],
      visitWebsite: "Visita il sito",
      servicesSubtitle: "Prodotti e servizi complementari per la tua imbarcazione",
      noServices: "Nessun servizio disponibile al momento",
      addService: "Aggiungi Servizio",
      accessCode: "Codice di accesso",
      enterCode: "Inserisci il codice",
      verify: "Verifica",
      newService: "Nuovo Servizio",
      photos: "Foto (max 3)",
      photo: "Foto",
      productName: "Nome del prodotto",
      productNamePlaceholder: "Es: Aria Condizionata Dometic",
      descLabel: "Descrizione",
      descPlaceholder: "Descrizione del prodotto o servizio...",
      createService: "Crea Servizio",
      wrongCode: "Codice errato"
    },
    seaTrial: {
      backHome: "Torna alla home",
      title: "RICHIEDI UNA",
      titleHighlight: "PROVA IN MARE",
      formName: "Nome completo *",
      formNamePlaceholder: "Il tuo nome",
      formEmail: "Email *",
      formEmailPlaceholder: "email@esempio.com",
      formPhone: "Telefono",
      formPhonePlaceholder: "+34 600 000 000",
      formBoat: "Modello barca",
      formBoatPlaceholder: "Es: Princess 62, Sunseeker 50...",
      formMessage: "Messaggio o commenti",
      formMessagePlaceholder: "Raccontaci di più sulla tua imbarcazione...",
      submitButton: "Richiedi Prova",
      successMessage: "Richiesta inviata con successo!",
      errorMessage: "Errore nell'invio. Riprova.",
      infoTitle: "Prova in Mare a Palma",
      infoDesc1: "Vieni a provare l'efficacia di Seakeeper nella baia di Palma. Effettueremo test statici e dinamici (tra 3 e 10 nodi), cercando sempre il peggior angolo d'onda —o usando la scia dei traghetti— per farti vedere come scompare il rollio.",
      infoDesc2: "Le nostre unità di prova utilizzano batterie al litio, permettendoti di sperimentare il comfort di un ancoraggio silenzioso, senza rumori di motore o generatore. Questo sistema a basso consumo è applicabile a qualsiasi lunghezza di scafo, da barche di 8 metri a grandi yacht di oltre 40 m. di lunghezza.",
      logisticsTitle: "Logistica",
      logisticsDesc: "Palma è perfettamente collegata con voli giornalieri dalla Spagna e dall'Europa. Ci adattiamo alla tua agenda e organizziamo la prova qualsiasi giorno, compresi i fine settimana.",
      location: "Baia di Palma di Maiorca"
    },
    seakeeperPage: {
      subtitle: "Stabilizzatore giroscopico",
      advantagesTitle: "VANTAGGI DI",
      advantagesDesc: "Scopri perché Seakeeper è il miglior investimento per la tua imbarcazione",
      requestSeaTrial: "Richiedi Prova in Mare",
      visitButton: "Visita Seakeeper",
      advantages: [
        { title: "Libertà di ancoraggio", desc: "Non dipendi da cale protette; puoi gettare l'ancora dove preferisci senza che le onde rovinino il momento." },
        { title: "Addio mal di mare", desc: "Elimina la paura degli ospiti soggetti al mal di mare, permettendo a tutti di rilassarsi e godersi la giornata." },
        { title: "Efficacia totale", desc: "A differenza delle pinne, funziona al 100% anche quando la barca è ferma o naviga a bassa velocità." },
        { title: "Navigazione senza limiti", desc: "Estendi i tuoi giorni in mare; condizioni che prima ti avrebbero tenuto in porto ora sono perfettamente navigabili." },
        { title: "Trasbordo sicuro", desc: "Facilita l'accesso dal tender evitando che l'imbarcazione principale oscilli bruscamente." },
        { title: "Piattaforma di pesca stabile", desc: "Fondamentale per la traina o la pesca di fondo, permettendo ai pescatori di concentrarsi sulla cattura." },
        { title: "Meno fatica fisica", desc: "Evita la stanchezza estrema prodotta dallo sforzo costante dei muscoli per compensare il rollio." },
        { title: "Manovre di carico sicure", desc: "Issa o vara la tua moto d'acqua o tender con totale stabilità, riducendo rischi di urti o incidenti." },
        { title: "Investimento redditizio", desc: "Un'imbarcazione con Seakeeper ha un valore di rivendita molto più alto e si vende molto più velocemente." },
        { title: "Comfort in porto", desc: "Anche ormeggiato, neutralizza il fastidioso rollio provocato dalle scie di altre barche o porti esposti." }
      ]
    },
    dockmatePage: {
      subtitle: "Telecomando per manovre",
      advantagesTitle: "VANTAGGI DI",
      visitButton: "Visita Dockmate",
      advantagesSubtitle: "Scopri perché Dockmate rivoluziona le manovre della tua imbarcazione",
      watchVideo: "Guarda il video",
      contactButton: "Contattaci",
      advantages: [
        { title: "Controllo totale", desc: "Ormeggia e disormeggia la tua imbarcazione da qualsiasi punto della barca con il telecomando wireless." },
        { title: "Manovre senza stress", desc: "Elimina la tensione delle manovre in porto, specialmente in spazi ristretti o con vento." },
        { title: "Sicurezza aumentata", desc: "Evita urti e graffi potendo controllare l'imbarcazione con totale precisione da prua o poppa." },
        { title: "Navigazione in solitario", desc: "Ideale per chi naviga da solo, senza bisogno di aiuto negli ormeggi." },
        { title: "Compatibile con la tua barca", desc: "Si adatta alla maggior parte dei sistemi di propulsione: fuoribordo, entrobordo, piede poppiero e pod drive." },
        { title: "Installazione professionale", desc: "Il nostro team tecnico certificato garantisce un'integrazione perfetta con la tua imbarcazione." },
        { title: "Facilità d'uso", desc: "Interfaccia intuitiva che permette di controllare la tua barca con massima precisione dal primo momento." },
        { title: "Risparmio di tempo", desc: "Riduce significativamente il tempo necessario per le manovre di ormeggio e disormeggio." }
      ]
    }
  }
};

// Language Context
const LanguageContext = createContext();

const useLanguage = () => useContext(LanguageContext);

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es');
  const t = translations[language];
  
  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext();

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('mextwave_token');
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  const verifyToken = async (savedToken) => {
    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      });
      setToken(savedToken);
      setIsAuthenticated(true);
    } catch {
      localStorage.removeItem('mextwave_token');
      setIsAuthenticated(false);
      setToken(null);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const newToken = response.data.token;
    localStorage.setItem('mextwave_token', newToken);
    setToken(newToken);
    setIsAuthenticated(true);
    return true;
  };

  const logout = () => {
    localStorage.removeItem('mextwave_token');
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Flag component with actual images
const Flag = ({ country, size = 20 }) => {
  const flagUrls = {
    es: "https://flagcdn.com/w40/es.png",
    en: "https://flagcdn.com/w40/gb.png",
    de: "https://flagcdn.com/w40/de.png",
    it: "https://flagcdn.com/w40/it.png"
  };
  return (
    <img 
      src={flagUrls[country]} 
      alt={country} 
      style={{ width: size, height: size * 0.67, objectFit: 'cover', borderRadius: 2 }}
    />
  );
};

// Language Selector
const LanguageSelector = ({ isLight = false }) => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  
  const languages = [
    { code: 'es', name: 'Español' },
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'it', name: 'Italiano' }
  ];

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          isLight ? 'hover:bg-gray-100' : 'hover:bg-white/10'
        }`}
        data-testid="language-selector"
      >
        <Flag country={language} />
        <span className={`text-sm font-medium ${isLight ? 'text-gray-700' : 'text-white'}`}>
          {language.toUpperCase()}
        </span>
        <ChevronDown size={16} className={`transition-transform ${isLight ? 'text-gray-700' : 'text-white'} ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl py-2 min-w-[160px] z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 transition-colors ${
                language === lang.code ? 'bg-blue-50 text-[#003399]' : 'text-gray-700'
              }`}
              data-testid={`lang-${lang.code}`}
            >
              <Flag country={lang.code} />
              <span className="text-sm">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Navigation
const Navigation = ({ isScrolled: propIsScrolled }) => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(propIsScrolled || false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/#about', label: t.nav.services },
    { href: '/#services', label: t.nav.about },
    { href: '/trabajos', label: t.nav.works, isRoute: true },
    { href: '/#contact', label: t.nav.contact },
  ];

  return (
    <>
      <nav 
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || propIsScrolled ? 'bg-white shadow-lg py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={isScrolled || propIsScrolled 
                ? "https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/c529ko5o_Next_Wave_Marine_logo_final.png"
                : "https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/jl3s23q0_Next_Wave_Marine_con%20blanco_logo_final.png"
              }
              alt="Nextwave Marine" 
              className="h-16"
            />
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-medium transition-colors ${
                    isScrolled || propIsScrolled ? 'text-gray-700 hover:text-[#003399]' : 'text-white hover:text-[#15BABD]'
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className={`font-medium transition-colors ${
                    isScrolled || propIsScrolled ? 'text-gray-700 hover:text-[#003399]' : 'text-white hover:text-[#15BABD]'
                  }`}
                >
                  {link.label}
                </a>
              )
            ))}
            <LanguageSelector isLight={isScrolled || propIsScrolled} />
          </div>

          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(true)}>
            <Menu className={isScrolled || propIsScrolled ? 'text-gray-900' : 'text-white'} size={28} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 bg-black/50 z-50 transition-opacity ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)} />
      
      <div className={`fixed top-0 right-0 h-full w-80 bg-white z-50 transform transition-transform ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/c529ko5o_Next_Wave_Marine_logo_final.png" 
              alt="Nextwave Marine" 
              className="h-10"
            />
            <button onClick={() => setMobileMenuOpen(false)}>
              <X size={24} />
            </button>
          </div>
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-lg font-medium text-gray-700 hover:text-[#003399] py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-lg font-medium text-gray-700 hover:text-[#003399] py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              )
            ))}
            <div className="pt-4 border-t">
              <LanguageSelector isLight={true} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();
  
  return (
    <section 
      id="home"
      className="min-h-screen flex items-center relative overflow-hidden"
    >
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/4hp7mysg_sleek-motor-yacht-cruises-across-deep-blue-water-bright-sunlight.jpg')`,
          transform: 'scaleX(1.25)',
          transformOrigin: 'center',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#001a4d]/40 via-[#002266]/30 to-[#003399]/20" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
        <div className="max-w-2xl">
          <p className="text-[#15BABD] font-semibold uppercase tracking-wider text-sm mb-4">{t.hero.subtitle}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {t.hero.title}{' '}
            <span className="text-[#15BABD]">{t.hero.highlight}</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-200 mb-10 max-w-xl">
            {t.hero.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#works" className="btn-primary-marine inline-flex items-center justify-center gap-2">
              {t.hero.cta1}
              <ChevronRight size={20} />
            </a>
            <a href="#contact" className="btn-secondary-marine inline-flex items-center justify-center gap-2">
              {t.hero.cta2}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const startValue = 0;
    const endValue = end;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(startValue + (endValue - startValue) * easeOutQuart);
      
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={counterRef}>
      {prefix}{count}{suffix}
    </span>
  );
};

const AboutSection = () => {
  const { t } = useLanguage();
  const [dockmateVideoOpen, setDockmateVideoOpen] = useState(false);
  const [seekeeperVideoOpen, setSeekeeperVideoOpen] = useState(false);

  return (
    <section id="about" className="py-24 bg-white">
      {/* Video Modals */}
      <VideoModal 
        isOpen={dockmateVideoOpen} 
        onClose={() => setDockmateVideoOpen(false)}
        videoUrl="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/gh0eom57_docmare.mp4"
        title="Dockmate"
      />
      <VideoModal 
        isOpen={seekeeperVideoOpen} 
        onClose={() => setSeekeeperVideoOpen(false)}
        videoUrl="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/744tip44_seakeeper%20%281%29.mp4"
        title="Seakeeper"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {t.about.title2}{' '}
            <span className="text-[#003399]">{t.about.highlight}</span>
          </h2>
          <p className="text-[#15BABD] font-bold uppercase tracking-wider text-xl mb-8 mt-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.about.title}</p>
          
          {/* 2 Cuadros de marcas - centrados */}
          <div className="flex justify-center mb-10 w-full">
            <div className="flex flex-col md:flex-row gap-10 md:gap-16 justify-center items-start">
              {/* Dockmate */}
              <div className="flex flex-col items-center justify-center w-72 md:w-96">
                <div className="flex items-center justify-center h-28 md:h-36 w-full">
                  <img src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/1tyq48cm_logo%20dogmate.png" alt="Dockmate" className="max-h-20 md:max-h-28 max-w-full object-contain" />
                </div>
                {/* Dockmate video thumbnail with overlay */}
                <button 
                  onClick={() => setDockmateVideoOpen(true)}
                  className="block w-full h-56 md:h-72 rounded-2xl overflow-hidden mb-4 relative group cursor-pointer"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/ke1eyl1v_foto%20dockmate.jpg" 
                    alt="Dockmate Video" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 flex items-center justify-center">
                    <span className="text-white text-xl font-semibold flex items-center gap-2 drop-shadow-lg">
                      {t.homepage.watchVideo}
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="17" x2="17" y2="7"></line>
                        <polyline points="7 7 17 7 17 17"></polyline>
                      </svg>
                    </span>
                  </div>
                </button>
                <Link to="/dockmate" className="px-8 py-2.5 bg-[#15BABD] text-white font-semibold rounded-lg hover:bg-[#003399] transition-colors duration-300">
                  {t.homepage.products}
                </Link>
              </div>
              
              {/* Seakeeper */}
              <div className="flex flex-col items-center justify-center w-72 md:w-96">
                <div className="flex items-center justify-center h-28 md:h-36 w-full">
                  <img src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/6ysagt9e_logo%20seakeeper.png" alt="Seakeeper" className="max-h-20 md:max-h-28 max-w-full object-contain" />
                </div>
                {/* Seakeeper video thumbnail with overlay */}
                <button 
                  onClick={() => setSeekeeperVideoOpen(true)}
                  className="block w-full h-56 md:h-72 rounded-2xl overflow-hidden mb-4 relative group cursor-pointer"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/6ekozjao_foto%20seakeeper.jpg" 
                    alt="Seakeeper Video" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 flex items-center justify-center">
                    <span className="text-white text-xl font-semibold flex items-center gap-2 drop-shadow-lg">
                      {t.homepage.watchVideo}
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="17" x2="17" y2="7"></line>
                        <polyline points="7 7 17 7 17 17"></polyline>
                      </svg>
                    </span>
                  </div>
                </button>
                <Link to="/seakeeper" className="px-8 py-2.5 bg-[#15BABD] text-white font-semibold rounded-lg hover:bg-[#003399] transition-colors duration-300">
                  {t.homepage.products}
                </Link>
              </div>
            </div>
          </div>

          {/* 3 Cuadros de características */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <div className="bg-[#003399] text-white p-5 rounded-xl text-center">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-base" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.homepage.officialService}</h4>
              <p className="text-xs text-blue-200 mt-1">{t.homepage.officialServiceDesc}</p>
            </div>
            <div className="bg-[#003399] text-white p-5 rounded-xl text-center">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Award className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-base" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.homepage.experience}</h4>
              <p className="text-xs text-blue-200 mt-1">{t.homepage.experienceDesc}</p>
            </div>
            <div className="bg-[#003399] text-white p-5 rounded-xl text-center">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Briefcase className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-base" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.homepage.quality}</h4>
              <p className="text-xs text-blue-200 mt-1">{t.homepage.qualityDesc}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Espacio antes de la línea */}
      <div className="h-12 bg-white"></div>

      {/* Línea turquesa separadora - ancho completo */}
      <div className="w-full h-[3px] bg-[#15BABD]"></div>

      {/* Otras Marcas */}
      <div className="bg-white py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {/* Otros Servicios - Izquierda */}
            <div className="text-center flex flex-col">
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-[#003399]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {t.homepage?.otherServices || "OTROS"} {t.homepage?.otherServicesHighlight || "SERVICIOS"}
              </h3>
              <Link 
                to="/otros-servicios"
                className="px-8 py-2.5 bg-[#15BABD] text-white font-semibold rounded-lg hover:bg-[#003399] transition-colors duration-300 inline-block mx-auto mb-6"
              >
                {t.homepage?.viewServices || "Ver Servicios"}
              </Link>
              <ul className="space-y-2 text-left max-w-xs mx-auto">
                {(t.otherBrands?.servicesList || [
                  "Aire Acondicionado",
                  "Packs de baterías de Litio",
                  "Generadores",
                  "Potabilizadoras",
                  "Etc."
                ]).map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700 text-sm">
                    <span className="w-2.5 h-2.5 bg-[#15BABD] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Opacmare - Centro */}
            <div className="text-center">
              <div className="flex flex-col justify-center items-center gap-6">
                <p className="text-2xl md:text-3xl font-bold text-[#003399]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.homepage?.opacmareSubtitle || "Pasarelas y Plataformas"}</p>
                <p className="text-gray-700 text-sm -mt-4 tracking-[0.35em]">{t.homepage?.opacmareTopBrands || "Primeras marcas"}</p>
              </div>
            </div>

            {/* Otras Marcas - Derecha */}
            <div className="text-center">
              <h3 className="text-2xl md:text-3xl font-bold mb-8 text-[#003399]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {t.homepage.otherBrands} {t.homepage.otherBrandsHighlight}
              </h3>
              <OtherBrandsAccordion />
            </div>
          </div>
        </div>
      </div>

      {/* Línea turquesa separadora inferior - ancho completo */}
      <div className="w-full h-[3px] bg-[#15BABD]"></div>

      {/* Espacio después de la línea */}
      <div className="h-12 bg-white"></div>

      {/* Prueba de Mar Section - Full Width */}
      <div className="relative w-full h-96 md:h-[460px]">
        <img 
          src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/9g8w3dad_prueba%20de%20mar%20foto%20a%20web.jpg" 
          alt="Prueba de Mar Seakeeper" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#003399]/60 flex flex-col items-center justify-center">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8 text-center px-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {t.homepage.seaTrialTitle}
          </h3>
          <Link to="/prueba-de-mar" className="px-10 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-[#003399] transition-colors duration-300 inline-block">
            {t.homepage.seaTrialButton}
          </Link>
        </div>
      </div>
    </section>
  );
};

// Other Brands Accordion
const OtherBrandsAccordion = () => {
  const { t } = useLanguage();
  const [openBrand, setOpenBrand] = useState(null);

  const brands = [
    {
      id: 'victron',
      name: 'Victron Energy',
      url: 'https://www.victronenergy.com',
      description: t.otherBrands?.victronDesc || "Victron Energy es el líder mundial en sistemas de energía independiente para embarcaciones. Sus inversores, cargadores, controladores solares y sistemas de monitorización ofrecen la máxima fiabilidad y eficiencia energética a bordo.",
      features: t.otherBrands?.victronFeatures || [
        "Inversores y cargadores de alta eficiencia",
        "Controladores de carga solar MPPT",
        "Monitorización remota con VRM Portal",
        "Sistemas de baterías de litio inteligentes",
        "Gestión integral de energía a bordo"
      ]
    },
    {
      id: 'ecosistems',
      name: 'EcoSistems',
      url: 'https://www.eco-sistems.com',
      description: t.otherBrands?.ecosistemsDesc || "Eco-Sistems Watermakers diseña, desarrolla y fabrica potabilizadoras/desalinizadoras de agua de mar que se caracterizan por sus eficientes sistemas de ahorro de energía. De 25 a 300 litros/hora.",
      features: t.otherBrands?.ecosistemsFeatures || []
    },
    {
      id: 'fischerpanda',
      name: 'Fischer Panda',
      url: 'https://www.fischerpanda.de',
      description: t.otherBrands?.fischerPandaDesc || "Fischer Panda es referente mundial en generadores marinos ultrasilenciosos y compactos. Sus sistemas de generación eléctrica están diseñados específicamente para el entorno náutico, ofreciendo máxima potencia con mínimo ruido y vibración.",
      features: t.otherBrands?.fischerPandaFeatures || [
        "Generadores marinos ultrasilenciosos",
        "Diseño compacto y ligero",
        "Sistemas de propulsión híbridos",
        "Aire acondicionado marino",
        "Servicio técnico especializado"
      ]
    },
    {
      id: 'dometic',
      name: 'Dometic',
      url: 'https://www.dometic.com',
      description: t.otherBrands?.dometicDesc || "Dometic es líder global en soluciones de confort para embarcaciones. Desde sistemas de climatización marina hasta refrigeración, cocinas y equipamiento de cubierta, Dometic hace la vida a bordo más cómoda.",
      features: t.otherBrands?.dometicFeatures || [
        "Aire acondicionado marino",
        "Refrigeración y congelación a bordo",
        "Cocinas y electrodomésticos marinos",
        "Toldos y equipamiento de cubierta",
        "Sistemas sanitarios marinos"
      ]
    }
  ];

  return (
    <div className="space-y-3 text-left max-w-md mx-auto">
      {brands.map((brand) => (
        <div key={brand.id} className="border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setOpenBrand(openBrand === brand.id ? null : brand.id)}
            className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
            data-testid={`brand-${brand.id}-toggle`}
          >
            <span className="font-bold text-[#003399] text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {brand.name}
            </span>
            <ChevronDown 
              size={20} 
              className={`text-[#003399] transition-transform duration-300 ${openBrand === brand.id ? 'rotate-180' : ''}`} 
            />
          </button>
          {openBrand === brand.id && (
            <div className="px-4 pb-4 bg-gray-50">
              <p className="text-gray-600 text-sm mb-3">{brand.description}</p>
              {brand.features.length > 0 && (
                <ul className="space-y-1.5 mb-3">
                  {brand.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <ChevronRight size={14} className="text-[#15BABD] mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              )}
              {brand.url && (
                <a href={brand.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-[#15BABD] text-white text-sm font-semibold rounded-lg hover:bg-[#003399] transition-colors">
                  {t.otherBrands?.visitWebsite || "Visitar web"}
                  <ChevronRight size={14} />
                </a>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Team Member Photo Component with grayscale effect on hover
const TeamMemberPhoto = ({ src, name }) => {
  return (
    <div className="flex flex-col items-center">
      <div className="w-20 h-20 rounded-xl overflow-hidden mb-2 transition-all duration-300 shadow-md hover:shadow-lg group">
        <img 
          src={src} 
          alt={name}
          className="w-full h-full object-cover transition-all duration-300 grayscale hover:grayscale-0"
        />
      </div>
      <span className="text-sm font-semibold text-[#003399]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{name}</span>
    </div>
  );
};

// Video Modal Component
const VideoModal = ({ isOpen, onClose, videoUrl, title }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
      >
        <X size={32} />
      </button>
      <div 
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <video 
          controls 
          autoPlay 
          className="w-full h-auto max-h-[80vh] rounded-lg"
          playsInline
        >
          <source src={videoUrl} type="video/mp4" />
          Tu navegador no soporta el video.
        </video>
        {title && <p className="text-white text-center mt-4 text-lg">{title}</p>}
      </div>
    </div>
  );
};

// Quienes Somos Section (replaces old Services cards)
const QuienesSomosSection = () => {
  const { t } = useLanguage();

  return (
    <section id="services" className="bg-gray-50">
      {/* Línea separadora azul */}
      <div className="w-full h-[3px] bg-[#003399]"></div>
      
      <div className="flex flex-col lg:flex-row">
        {/* Image - Full height on left */}
        <div className="lg:w-1/2 h-[500px] lg:h-auto">
          <img 
            src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/wcfmu6xt_yate%20.jpg" 
            alt="Nextwave Marine Yacht" 
            className="w-full h-full object-cover"
          />
        </div>

        {/* Text Content - Right side */}
        <div className="lg:w-1/2 py-16 px-8 md:px-16 flex flex-col justify-center">
          <p className="text-[#15BABD] font-semibold uppercase tracking-wider text-sm mb-4">{t.nav.about}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {t.quienesSomos?.title || "QUIÉNES"}{' '}
            <span className="text-[#003399]">{t.quienesSomos?.highlight || "SOMOS"}</span>
          </h2>
          
          <div className="space-y-4">
            <p className="text-gray-700 text-base leading-relaxed">
              {t.quienesSomos?.description1}
            </p>
            <p className="text-gray-700 text-base leading-relaxed">
              {t.quienesSomos?.description2}
            </p>
            <p className="text-gray-700 text-base leading-relaxed">
              {t.quienesSomos?.description3}
            </p>
            <p className="text-gray-700 text-base leading-relaxed">
              {t.quienesSomos?.description4}
            </p>
            <p className="text-gray-700 text-base leading-relaxed font-semibold text-[#003399]">
              {t.quienesSomos?.description5}
            </p>
            
            {/* Button and Team Icons in same row */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 pt-8">
              <div>
                <Link 
                  to="/equipo" 
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#15BABD] text-white font-semibold rounded-lg hover:bg-[#003399] transition-colors"
                >
                  {t.quienesSomos?.button || "Conózcanos mejor"}
                  <ChevronRight size={20} />
                </Link>
              </div>
              
              {/* Team Photos */}
              <div className="flex items-center gap-6">
                <TeamMemberPhoto 
                  src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/9mmia5cc_jaime.jpg"
                  name="Jaime"
                />
                <TeamMemberPhoto 
                  src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/g1lya1ef_miriam.jpg"
                  name="Miriam"
                />
                <TeamMemberPhoto 
                  src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/cawxlqna_marcus.jpg"
                  name="Markus"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Brands Section
const BrandsSection = ({ setDockmateVideoOpen, setSeekeeperVideoOpen }) => {
  const { t } = useLanguage();
  
  const brands = [
    "YAMAHA", "MERCURY", "GARMIN", "RAYMARINE", "SUZUKI", 
    "VOLVO PENTA", "QUICKSILVER", "LOWRANCE", "SIMRAD", "PLASTIMO"
  ];

  return (
    <section id="brands" className="py-24 bg-[#003399]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-[#15BABD] font-semibold uppercase tracking-wider text-sm mb-4">{t.brands.subtitle}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {t.brands.title}{' '}
            <span className="text-[#15BABD]">{t.brands.highlight}</span>
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">{t.brands.description}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {brands.map((brand, index) => (
            <div key={index} className="bg-white/10 backdrop-blur rounded-xl p-6 flex items-center justify-center hover:bg-white/20 transition-colors">
              <span className="text-white font-bold text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{brand}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Image Carousel for Works
const WorkImageCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) return null;

  return (
    <div className="relative w-full aspect-square overflow-hidden rounded-xl group">
      {images.map((img, idx) => (
        <img
          key={idx}
          src={img}
          alt={`Imagen ${idx + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            idx === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
      {images.length > 1 && (
        <>
          <button
            onClick={() => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => setCurrentIndex((prev) => (prev + 1) % images.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, idx) => (
              <span
                key={idx}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-[#15BABD]' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Login Modal
const LoginModal = ({ onClose, onSuccess }) => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    try {
      await login(email, password);
      onSuccess();
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-center w-16 h-16 bg-[#003399]/10 rounded-full mx-auto mb-6">
          <LogIn className="text-[#003399]" size={32} />
        </div>
        <h3 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          {t.login.title}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.login.email}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input-marine"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.login.password}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input-marine"
                required
              />
            </div>
            {error && <p className="text-red-500 text-center text-sm">{t.login.error}</p>}
            <div className="flex gap-4 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary-marine flex-1">
                {t.login.cancel}
              </button>
              <button type="submit" disabled={loading} className="btn-primary-marine flex-1">
                {loading ? '...' : t.login.submit}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add/Edit Work Modal
const WorkModal = ({ work, onClose, onSuccess }) => {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [title, setTitle] = useState(work?.title || '');
  const [description, setDescription] = useState(work?.description || '');
  const [date, setDate] = useState(work?.date || new Date().toISOString().split('T')[0]);
  const [images, setImages] = useState(work?.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      alert(language === 'es' ? 'Máximo 5 imágenes' : 'Maximum 5 images');
      return;
    }

    for (const file of files) {
      if (images.length >= 5) break;
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/upload-image`, formData);
        setImages(prev => [...prev, response.data.url]);
      } catch (error) {
        console.error('Error uploading:', error);
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (images.length === 0) {
      alert(language === 'es' ? 'Añade al menos una imagen' : 'Add at least one image');
      return;
    }
    setSaving(true);

    try {
      const payload = { title, description, date, images };
      if (work) {
        await axios.put(`${API}/works/${work.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/works`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving work:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full shadow-2xl my-8">
        <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          {work ? t.addWork.editTitle : t.addWork.title}
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.addWork.name}</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="form-input-marine"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.addWork.description}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="form-input-marine resize-none"
                rows={3}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.addWork.date}</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="form-input-marine"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.addWork.images}</label>
              <div className="grid grid-cols-5 gap-3 mb-3">
                {images.map((img, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {uploading && (
                  <div className="aspect-square rounded-lg bg-gray-100 flex items-center justify-center">
                    <div className="loading-spinner" />
                  </div>
                )}
                {images.length < 5 && !uploading && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 hover:border-[#003399] hover:bg-blue-50 transition-colors"
                  >
                    <Plus size={24} className="text-gray-400" />
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary-marine flex-1">
                {t.addWork.cancel}
              </button>
              <button type="submit" disabled={saving} className="btn-primary-marine flex-1">
                {saving ? '...' : (work ? t.addWork.save : t.addWork.add)}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Works Section (Homepage - shows only last 2 works)
const WorksSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWorks = async () => {
    try {
      const response = await axios.get(`${API}/works`);
      setWorks(response.data.slice(0, 2)); // Only show last 2
    } catch (error) {
      console.error('Error fetching works:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorks();
  }, []);

  return (
    <section id="works" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="text-[#15BABD] font-semibold uppercase tracking-wider text-sm mb-4">{t.works.subtitle}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {t.works.title}{' '}
            <span className="text-[#003399]">{t.works.highlight}</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-xl mx-auto">{t.works.description}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><div className="loading-spinner" />
          </div>
        ) : works.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-2xl">
            <Image size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-6">{t.works.empty}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {works.map((work) => (
              <WorkCardSimple key={work.id} work={work} />
            ))}
          </div>
        )}

        {/* View All Works Button */}
        <div className="flex justify-center mt-12">
          <Link 
            to="/trabajos"
            className="btn-primary-marine inline-flex items-center gap-2"
          >
            {t.works.viewAll || 'Ver todos los trabajos'}
            <ChevronRight size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Simple Work Card (no delete/edit buttons - for homepage)
const WorkCardSimple = ({ work }) => {
  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = work.images || [];
  
  // Get translated content based on current language
  const getTranslatedField = (field) => {
    if (work.translations && work.translations[language] && work.translations[language][field]) {
      return work.translations[language][field];
    }
    return work[field];
  };
  
  const displayTitle = getTranslatedField('title');
  const displayDescription = getTranslatedField('description');

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
      <div className="relative aspect-square bg-gray-100 overflow-hidden">
        {images.length > 0 ? (
          <>
            {images.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`${displayTitle} - ${idx + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                  idx === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white text-[#003399] rounded-full flex items-center justify-center shadow-lg transition-all"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setCurrentIndex((prev) => (prev + 1) % images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white text-[#003399] rounded-full flex items-center justify-center shadow-lg transition-all"
                >
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentIndex ? 'bg-[#15BABD]' : 'bg-white/60'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image size={48} className="text-gray-300" />
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Calendar size={14} />
          {work.date}
        </div>
        {displayTitle && (
          <h4 className="font-bold text-gray-800 mb-3">{displayTitle}</h4>
        )}
        <div className="bg-gray-50 rounded-lg p-4 min-h-[80px]">
          <p className="text-gray-700 text-sm leading-relaxed">{displayDescription}</p>
        </div>
      </div>
    </div>
  );
};

// Works Page (shows all works with admin functionality)
const WorksPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeAction, setCodeAction] = useState(null);
  const [pendingWorkId, setPendingWorkId] = useState(null);
  const [editingWork, setEditingWork] = useState(null);

  const ADMIN_CODE = "6792";

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchWorks();
  }, []);

  const fetchWorks = async () => {
    try {
      const response = await axios.get(`${API}/works`);
      setWorks(response.data);
    } catch (error) {
      console.error('Error fetching works:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSuccess = () => {
    setShowCodeModal(false);
    if (codeAction === 'add') {
      setShowAddModal(true);
    } else if (codeAction === 'delete' && pendingWorkId) {
      performDelete(pendingWorkId);
    } else if (codeAction === 'edit' && editingWork) {
      setShowAddModal(true);
    }
    setCodeAction(null);
    setPendingWorkId(null);
  };

  const performDelete = async (workId) => {
    try {
      await axios.delete(`${API}/works/${workId}`);
      fetchWorks();
    } catch (error) {
      console.error('Error deleting work:', error);
    }
  };

  const requestAdd = () => {
    setCodeAction('add');
    setShowCodeModal(true);
  };

  const requestDelete = (workId) => {
    setPendingWorkId(workId);
    setCodeAction('delete');
    setShowCodeModal(true);
  };

  const handleWorkSuccess = () => {
    setShowAddModal(false);
    setEditingWork(null);
    fetchWorks();
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead language={language} />
      <Navigation isScrolled={true} />
      
      {/* Hero */}
      <div className="pt-24 pb-16 bg-[#003399] relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/4hp7mysg_sleek-motor-yacht-cruises-across-deep-blue-water-bright-sunlight.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-16">
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors"
          >
            <ChevronLeft size={20} />
            {t.nav.home}
          </button>
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            {t.works.title} <span className="text-[#15BABD]">{t.works.highlight}</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl">
            {t.works.description}
          </p>
        </div>
      </div>

      {/* Works Grid */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" />
          </div>
        ) : works.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-2xl">
            <Image size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-6">{t.works.empty}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {works.map((work) => (
              <WorkCard 
                key={work.id} 
                work={work} 
                onDelete={() => requestDelete(work.id)}
              />
            ))}
          </div>
        )}

        {/* Add Work Button */}
        <div className="flex justify-center mt-12">
          <button 
            onClick={requestAdd}
            className="text-gray-400 hover:text-[#003399] text-sm flex items-center gap-2 transition-colors"
          >
            <Plus size={16} />
            {t.works.addWork}
          </button>
        </div>
      </div>

      {/* Code Modal */}
      {showCodeModal && (
        <CodeModal 
          onSuccess={handleCodeSuccess}
          onClose={() => {
            setShowCodeModal(false);
            setCodeAction(null);
            setPendingWorkId(null);
            setEditingWork(null);
          }}
          adminCode={ADMIN_CODE}
        />
      )}

      {/* Add/Edit Work Modal */}
      {showAddModal && (
        <SimpleWorkModal
          work={editingWork}
          onClose={() => {
            setShowAddModal(false);
            setEditingWork(null);
          }}
          onSuccess={handleWorkSuccess}
        />
      )}

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

// Code Modal Component
const CodeModal = ({ onSuccess, onClose, adminCode }) => {
  const { language } = useLanguage();
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code === adminCode) {
      onSuccess();
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl">
        <h3 className="text-xl font-bold text-center mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          {language === 'es' ? 'CÓDIGO DE ACCESO' : language === 'de' ? 'ZUGANGSCODE' : language === 'it' ? 'CODICE DI ACCESSO' : 'ACCESS CODE'}
        </h3>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="****"
            className={`form-input-marine text-center text-2xl tracking-widest ${error ? 'border-red-500' : ''}`}
            maxLength={4}
            autoFocus
          />
          {error && (
            <p className="text-red-500 text-center text-sm mt-2">
              {language === 'es' ? 'Código incorrecto' : language === 'de' ? 'Falscher Code' : language === 'it' ? 'Codice errato' : 'Wrong code'}
            </p>
          )}
          <div className="flex gap-4 mt-6">
            <button type="button" onClick={onClose} className="btn-secondary-marine flex-1 !border-gray-300 !text-gray-600">
              {language === 'es' ? 'Cancelar' : language === 'de' ? 'Abbrechen' : language === 'it' ? 'Annulla' : 'Cancel'}
            </button>
            <button type="submit" className="btn-primary-marine flex-1">
              {language === 'es' ? 'Acceder' : language === 'de' ? 'Zugang' : language === 'it' ? 'Accedi' : 'Enter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Work Card Component
const WorkCard = ({ work, onDelete, onEdit }) => {
  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = work.images || [];
  
  // Get translated content based on current language
  const getTranslatedField = (field) => {
    if (work.translations && work.translations[language] && work.translations[language][field]) {
      return work.translations[language][field];
    }
    return work[field];
  };
  
  const displayTitle = getTranslatedField('title');
  const displayDescription = getTranslatedField('description');

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
      {/* Square Image Container */}
      <div className="relative aspect-square bg-gray-100 overflow-hidden">
        {images.length > 0 ? (
          <>
            {images.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`${displayTitle} - ${idx + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                  idx === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white text-[#003399] rounded-full flex items-center justify-center shadow-lg transition-all"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setCurrentIndex((prev) => (prev + 1) % images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white text-[#003399] rounded-full flex items-center justify-center shadow-lg transition-all"
                >
                  <ChevronRight size={20} />
                </button>
                {/* Dots indicator */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentIndex ? 'bg-[#15BABD]' : 'bg-white/60'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image size={48} className="text-gray-300" />
          </div>
        )}
        
        {/* Delete button */}
        <button
          onClick={onDelete}
          className="absolute top-3 right-3 w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all opacity-70 hover:opacity-100"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* Info Section */}
      <div className="p-5">
        {/* Date */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Calendar size={14} />
          {work.date}
        </div>
        
        {/* Title */}
        {displayTitle && (
          <h4 className="font-bold text-gray-800 mb-3">{displayTitle}</h4>
        )}
        
        {/* Description Box */}
        <div className="bg-gray-50 rounded-lg p-4 min-h-[80px]">
          <p className="text-gray-700 text-sm leading-relaxed">{displayDescription}</p>
        </div>
      </div>
    </div>
  );
};

// Simple Work Modal (Add/Edit)
const SimpleWorkModal = ({ work, onClose, onSuccess }) => {
  const { t, language } = useLanguage();
  const [title, setTitle] = useState(work?.title || '');
  const [description, setDescription] = useState(work?.description || '');
  const [date, setDate] = useState(work?.date || new Date().toISOString().split('T')[0]);
  const [images, setImages] = useState(work?.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      alert(language === 'es' ? 'Máximo 5 imágenes' : 'Maximum 5 images');
      return;
    }

    for (const file of files) {
      if (images.length >= 5) break;
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/upload-image`, formData);
        setImages(prev => [...prev, response.data.url]);
      } catch (error) {
        console.error('Error uploading:', error);
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (images.length === 0) {
      alert(language === 'es' ? 'Añade al menos una imagen' : 'Add at least one image');
      return;
    }
    setSaving(true);

    try {
      const token = localStorage.getItem('mextwave_token');
      const payload = { 
        title: title || 'Trabajo', 
        description, 
        date, 
        images 
      };
      
      if (work) {
        await axios.put(`${API}/works/${work.id}`, payload, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      } else {
        await axios.post(`${API}/works`, payload, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving work:', error);
      // Try without auth header
      try {
        const payload = { 
          title: title || 'Trabajo', 
          description, 
          date, 
          images 
        };
        if (work) {
          await axios.put(`${API}/works/${work.id}`, payload);
        } else {
          await axios.post(`${API}/works`, payload);
        }
        onSuccess();
      } catch (err) {
        alert(language === 'es' ? 'Error al guardar' : 'Error saving');
      }
    } finally {
      setSaving(false);
    }
  };

  const labels = {
    es: { modalTitle: work ? 'EDITAR TRABAJO' : 'AÑADIR TRABAJO', date: 'Fecha', workTitle: 'Título (máx. 100 caracteres)', desc: 'Descripción (máx. 500 caracteres)', images: 'Imágenes (máx. 5, cuadradas)', cancel: 'Cancelar', save: 'Guardar' },
    en: { modalTitle: work ? 'EDIT WORK' : 'ADD WORK', date: 'Date', workTitle: 'Title (max. 100 characters)', desc: 'Description (max. 500 characters)', images: 'Images (max. 5, square)', cancel: 'Cancel', save: 'Save' },
    de: { modalTitle: work ? 'ARBEIT BEARBEITEN' : 'ARBEIT HINZUFÜGEN', date: 'Datum', workTitle: 'Titel (max. 100 Zeichen)', desc: 'Beschreibung (max. 500 Zeichen)', images: 'Bilder (max. 5, quadratisch)', cancel: 'Abbrechen', save: 'Speichern' },
    it: { modalTitle: work ? 'MODIFICA LAVORO' : 'AGGIUNGI LAVORO', date: 'Data', workTitle: 'Titolo (max. 100 caratteri)', desc: 'Descrizione (max. 500 caratteri)', images: 'Immagini (max. 5, quadrate)', cancel: 'Annulla', save: 'Salva' }
  };
  const l = labels[language] || labels.en;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl my-8">
        <h3 className="text-2xl font-bold mb-6 text-center" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          {l.modalTitle}
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-5">
            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{l.images}</label>
              <div className="grid grid-cols-5 gap-2">
                {images.map((img, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {uploading && (
                  <div className="aspect-square rounded-lg bg-gray-100 flex items-center justify-center">
                    <div className="loading-spinner !w-4 !h-4 !border-[#003399]" />
                  </div>
                )}
                {images.length < 5 && !uploading && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-[#003399] hover:bg-blue-50 transition-colors"
                  >
                    <Plus size={20} className="text-gray-400" />
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{l.date}</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="form-input-marine"
                required
              />
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{l.workTitle}</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, 100))}
                className="form-input-marine"
                maxLength={100}
                placeholder={language === 'es' ? 'Título del trabajo...' : 'Work title...'}
                required
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{title.length}/100</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{l.desc}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                className="form-input-marine resize-none"
                rows={3}
                maxLength={500}
                placeholder={language === 'es' ? 'Breve descripción del trabajo...' : 'Brief work description...'}
                required
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{description.length}/500</p>
            </div>

            <div className="flex gap-4 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary-marine flex-1 !border-gray-300 !text-gray-600">
                {l.cancel}
              </button>
              <button type="submit" disabled={saving} className="btn-primary-marine flex-1">
                {saving ? '...' : l.save}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', recipient: 'info' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  const recipients = {
    jaime: { name: 'Jaime Capell', email: 'jaime@nextwave-marine.com', role: t.contact?.form?.jaimeRole || 'Responsable de Ventas y Proyectos' },
    miriam: { name: 'Miriam Cirella', email: 'miriam@nextwave-marine.com', role: t.contact?.form?.miriamRole || 'Coordinadora de Operaciones y Administración' },
    markus: { name: 'Markus Ruesseler', email: 'markus@nextwave-marine.com', role: t.contact?.form?.markusRole || 'Responsable Técnico' },
    info: { name: 'NextWave Marine', email: 'info@nextwave-marine.com', role: t.contact?.form?.generalTeam || 'Equipo General' }
  };

  useEffect(() => {
    const checkRecipient = () => {
      const fullUrl = window.location.href;
      const match = fullUrl.match(/[?&]to=([^&#]*)/);
      if (match && match[1] && recipients[match[1]]) {
        const to = match[1];
        setSelectedRecipient(to);
        setFormData(prev => ({ ...prev, recipient: to }));
        // Scroll to contact section
        setTimeout(() => {
          const contactSection = document.getElementById('contact');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
      }
    };
    checkRecipient();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const dataToSend = {
        ...formData,
        recipientEmail: recipients[formData.recipient]?.email || 'info@nextwave-marine.com',
        recipientName: recipients[formData.recipient]?.name || 'NextWave Marine'
      };
      await axios.post(`${API}/contact`, dataToSend);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '', recipient: 'info' });
      setSelectedRecipient(null);
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-[#15BABD] font-semibold uppercase tracking-wider text-sm mb-4">{t.contact.subtitle}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {t.contact.title}{' '}
              <span className="text-[#003399]">{t.contact.highlight}</span>?
            </h2>
            <p className="text-gray-600 text-lg mb-8">{t.contact.description}</p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#003399]/10 rounded-xl flex items-center justify-center">
                  <Phone className="text-[#003399]" size={24} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">{t.contact.form.phone}</p>
                  <a href="tel:+34625034234" className="font-semibold text-gray-900 hover:text-[#003399]">+34 625 03 42 34</a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#003399]/10 rounded-xl flex items-center justify-center">
                  <Mail className="text-[#003399]" size={24} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Email</p>
                  <a href="mailto:info@nextwave-marine.com" className="font-semibold text-gray-900 hover:text-[#003399] block">info@nextwave-marine.com</a>
                  
                  <a href="mailto:jaime@nextwave-marine.com" className="text-gray-900 hover:text-[#003399] block mt-2">jaime@nextwave-marine.com</a>
                  <p className="text-gray-500 text-xs">{t.contact?.form?.jaimeRole || "Ventas y Proyectos"}</p>
                  
                  <a href="mailto:miriam@nextwave-marine.com" className="text-gray-900 hover:text-[#003399] block mt-2">miriam@nextwave-marine.com</a>
                  <p className="text-gray-500 text-xs">{t.contact?.form?.miriamRole || "Operaciones"}</p>
                  
                  <a href="mailto:markus@nextwave-marine.com" className="text-gray-900 hover:text-[#003399] block mt-2">markus@nextwave-marine.com</a>
                  <p className="text-gray-500 text-xs">{t.contact?.form?.markusRole || "Técnico"}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#003399]/10 rounded-xl flex items-center justify-center">
                  <MapPin className="text-[#003399]" size={24} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">{t.contact?.form?.locationLabel || "Ubicación"}</p>
                  <p className="font-semibold text-gray-900">C/ Francesc Vallduvi, 18</p>
                  <p className="text-gray-600 text-sm">07011 Pol. Can Valero, Palma</p>
                  <p className="text-gray-600 text-sm">Mallorca - Baleares, España</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            {selectedRecipient && (
              <div className="mb-6 p-4 bg-[#003399]/10 rounded-lg border border-[#003399]/20">
                <p className="text-sm text-gray-600">{t.contact?.form?.messageTo || "Mensaje para:"}</p>
                <p className="font-semibold text-[#003399]">{recipients[selectedRecipient]?.name}</p>
                <p className="text-xs text-gray-500">{recipients[selectedRecipient]?.role}</p>
                <button 
                  type="button"
                  onClick={() => { setSelectedRecipient(null); setFormData(prev => ({ ...prev, recipient: 'info' })); }}
                  className="text-xs text-[#15BABD] hover:underline mt-1"
                >
                  Cambiar destinatario
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {!selectedRecipient && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.form.recipient || "Destinatario"}</label>
                    <select
                      value={formData.recipient}
                      onChange={(e) => setFormData({...formData, recipient: e.target.value})}
                      className="form-input-marine"
                    >
                      <option value="info">{t.contact.form.generalTeam || "Equipo General"}</option>
                      <option value="jaime">Jaime Capell - {t.contact.form.jaimeRole || "Ventas y Proyectos"}</option>
                      <option value="miriam">Miriam Cirella - {t.contact.form.miriamRole || "Operaciones"}</option>
                      <option value="markus">Markus Ruesseler - {t.contact.form.markusRole || "Técnico"}</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.form.name}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="form-input-marine"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.form.email}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="form-input-marine"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.form.phone}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="form-input-marine"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.form.message}</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    rows={4}
                    className="form-input-marine resize-none"
                  />
                </div>

                <button type="submit" disabled={isSubmitting} className="btn-primary-marine w-full flex items-center justify-center gap-2">
                  {isSubmitting ? <div className="loading-spinner" /> : <><Send size={20} /> {t.contact.form.send}</>}
                </button>

                {submitStatus === 'success' && <p className="text-green-600 text-center">{t.contact.form.success}</p>}
                {submitStatus === 'error' && <p className="text-red-600 text-center">{t.contact.form.error}</p>}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  const { openModal } = useLegalModal();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative text-white py-16 overflow-hidden">
      {/* Background image - rough sea */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/1426718/pexels-photo-1426718.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
        }}
      />
      {/* Dark blue overlay */}
      <div className="absolute inset-0 bg-[#001a4d]/90" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/jl3s23q0_Next_Wave_Marine_con%20blanco_logo_final.png" 
                alt="Nextwave Marine" 
                className="h-16"
              />
            </div>
            <p className="text-blue-200 mb-4">{t.footer.description}</p>
            <p className="text-[#15BABD] font-medium">{t.footer.slogan}</p>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.footer.services}</h4>
            <ul className="space-y-2">
              <li><a href="#home" className="text-blue-200 hover:text-white transition-colors">{t.nav.home}</a></li>
              <li><a href="#about" className="text-blue-200 hover:text-white transition-colors">{t.nav.services}</a></li>
              <li><a href="#services" className="text-blue-200 hover:text-white transition-colors">{t.nav.about}</a></li>
              <li><a href="#brands" className="text-blue-200 hover:text-white transition-colors">{t.nav.brands}</a></li>
              <li><a href="#works" className="text-blue-200 hover:text-white transition-colors">{t.nav.works}</a></li>
              <li><a href="#contact" className="text-blue-200 hover:text-white transition-colors">{t.nav.contact}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.footer.company}</h4>
            <ul className="space-y-2">
              <li><button onClick={() => openModal('avisoLegal')} className="text-blue-200 hover:text-white transition-colors">{t.footer.legal}</button></li>
              <li><button onClick={() => openModal('privacidad')} className="text-blue-200 hover:text-white transition-colors">{t.footer.privacy}</button></li>
              <li><button onClick={() => openModal('cookies')} className="text-blue-200 hover:text-white transition-colors">{t.footer.cookies}</button></li>
              <li className="flex items-start gap-2 mt-2">
                <MapPin size={16} className="text-[#15BABD] mt-0.5 flex-shrink-0" />
                <span className="text-blue-200 text-sm">C/ Francesc Vallduvi, 18<br />07011 Pol. Can Valero<br />Palma - Mallorca<br />Baleares, España</span>
              </li>
              <li className="flex items-center gap-2 mt-2">
                <Phone size={16} className="text-[#15BABD] flex-shrink-0" />
                <a href="tel:+34625034234" className="text-blue-200 text-sm hover:text-white">+34 625 03 42 34</a>
              </li>
            </ul>
            <div className="flex gap-4 mt-6">
              <a href="https://instagram.com/mextwavemarine" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#15BABD] transition-colors">
                <Instagram size={20} />
              </a>
              <a href="https://facebook.com/mextwavemarine" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#15BABD] transition-colors">
                <Facebook size={20} />
              </a>
              <a href="https://youtube.com/@nextwavemarine" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#15BABD] transition-colors">
                <Youtube size={20} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.footer.contact}</h4>
            
            {/* Team contacts */}
            <div className="space-y-4">
              <a href="/?to=jaime#contact" className="block p-3 border border-blue-400/30 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                <p className="text-white font-semibold text-sm">Jaime Capell</p>
                <p className="text-[#15BABD] text-xs">{t.contact?.form?.jaimeRole || "Ventas y Proyectos"}</p>
                <p className="text-blue-200 text-sm">jaime@nextwave-marine.com</p>
                <p className="text-blue-200 text-sm">+34 600 95 67 92</p>
                <div className="flex gap-2 mt-1">
                  <img src="https://flagcdn.com/w20/es.png" alt="Español" className="h-4" />
                  <img src="https://flagcdn.com/w20/gb.png" alt="English" className="h-4" />
                </div>
              </a>
              <a href="/?to=miriam#contact" className="block p-3 border border-blue-400/30 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                <p className="text-white font-semibold text-sm">Miriam Cirella</p>
                <p className="text-[#15BABD] text-xs">{t.contact?.form?.miriamRole || "Operaciones"}</p>
                <p className="text-blue-200 text-sm">miriam@nextwave-marine.com</p>
                <p className="text-blue-200 text-sm">+34 605 77 95 46</p>
                <div className="flex gap-2 mt-1">
                  <img src="https://flagcdn.com/w20/it.png" alt="Italiano" className="h-4" />
                  <img src="https://flagcdn.com/w20/es.png" alt="Español" className="h-4" />
                  <img src="https://flagcdn.com/w20/gb.png" alt="English" className="h-4" />
                </div>
              </a>
              <a href="/?to=markus#contact" className="block p-3 border border-blue-400/30 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                <p className="text-white font-semibold text-sm">Markus Ruesseler</p>
                <p className="text-[#15BABD] text-xs">{t.contact?.form?.markusRole || "Técnico"}</p>
                <p className="text-blue-200 text-sm">markus@nextwave-marine.com</p>
                <p className="text-blue-200 text-sm">+34 652 53 14 80</p>
                <div className="flex gap-2 mt-1">
                  <img src="https://flagcdn.com/w20/de.png" alt="Deutsch" className="h-4" />
                  <img src="https://flagcdn.com/w20/gb.png" alt="English" className="h-4" />
                  <img src="https://flagcdn.com/w20/es.png" alt="Español" className="h-4" />
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-blue-400/30 pt-8 text-center">
          <p className="text-blue-300/70 text-xs mb-2">
            desarrollado por:
          </p>
          <a 
            href="https://www.pxs.es" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-orange-500 hover:text-orange-400 transition-colors font-medium text-xs"
          >
            PXS Comunicación
          </a>
          <p className="text-blue-200 text-xs mt-3">© {currentYear} NextWave Marine. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Button
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/34600956792?text=Hola%20Mextwave%20Marine.%20Necesito%20información"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-40"
      data-testid="whatsapp-btn"
    >
      <MessageCircle size={28} className="text-white" />
    </a>
  );
};

// Cookie Consent Banner
const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    // Don't show on vCard pages
    if (location.pathname === '/jaime' || location.pathname === '/miriam' || location.pathname === '/markus') {
      return;
    }
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1000);
    }
  }, [location.pathname]);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleCustomize = () => {
    // Por ahora, redirigir a la política de cookies
    window.location.href = '/politica-cookies';
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
      {/* Banner - solo este elemento captura eventos */}
      <div className={`pointer-events-auto w-full max-w-2xl mx-4 mb-6 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-6 md:p-8 transform transition-transform duration-300 ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          {t.cookieBanner?.title || "Valoramos tu privacidad"}
        </h3>
        <p className="text-gray-600 text-sm md:text-base mb-6 leading-relaxed">
          {t.cookieBanner?.text || "Usamos cookies para mejorar su experiencia de navegación, mostrarle anuncios o contenidos personalizados y analizar nuestro tráfico."}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleCustomize}
            className="px-6 py-3 border-2 border-[#003399] text-[#003399] font-semibold rounded-lg hover:bg-[#003399]/10 transition-colors"
          >
            {t.cookieBanner?.customize || "Personalizar"}
          </button>
          <button
            onClick={handleReject}
            className="px-6 py-3 border-2 border-[#003399] text-[#003399] font-semibold rounded-lg hover:bg-[#003399]/10 transition-colors"
          >
            {t.cookieBanner?.rejectAll || "Rechazar todo"}
          </button>
          <button
            onClick={handleAccept}
            className="px-6 py-3 bg-[#003399] text-white font-semibold rounded-lg hover:bg-[#002266] transition-colors"
          >
            {t.cookieBanner?.acceptAll || "Aceptar todo"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Legal Modal Context
const LegalModalContext = createContext();

const LegalModalProvider = ({ children }) => {
  const [activeModal, setActiveModal] = useState(null);
  
  const openModal = (modalType) => setActiveModal(modalType);
  const closeModal = () => setActiveModal(null);
  
  return (
    <LegalModalContext.Provider value={{ activeModal, openModal, closeModal }}>
      {children}
    </LegalModalContext.Provider>
  );
};

const useLegalModal = () => useContext(LegalModalContext);

// Aviso Legal Modal
const AvisoLegalModal = () => {
  const { activeModal, closeModal } = useLegalModal();
  const { t } = useLanguage();
  
  if (activeModal !== 'avisoLegal') return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-[#003399] text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {t.legalModal.title}
          </h2>
          <button 
            onClick={closeModal}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(85vh-100px)]">
          <div className="space-y-6">
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.legalModal.section1Title}</h3>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <p><strong>{t.legalModal.company}:</strong> NextWave Marine SL</p>
                <p><strong>CIF:</strong> B26952044</p>
                <p><strong>{t.legalModal.address}:</strong> Son Puig 19, Palma (07011), Illes Balears, España</p>
                <p><strong>{t.legalModal.phone}:</strong> <a href="tel:+34600956792" className="text-[#15BABD] hover:underline">+34 600 95 67 92</a></p>
                <p><strong>{t.legalModal.email}:</strong> <a href="mailto:info@nextwave-marine.com" className="text-[#15BABD] hover:underline">info@nextwave-marine.com</a></p>
              </div>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.legalModal.section2Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.legalModal.section2Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.legalModal.section3Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.legalModal.section3Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.legalModal.section4Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.legalModal.section4Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.legalModal.section5Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.legalModal.section5Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.legalModal.section6Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.legalModal.section6Text}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Política de Privacidad Modal
const PrivacidadModal = () => {
  const { activeModal, closeModal } = useLegalModal();
  const { t } = useLanguage();
  
  if (activeModal !== 'privacidad') return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-[#003399] text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {t.privacyModal.title}
          </h2>
          <button 
            onClick={closeModal}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(85vh-100px)]">
          <div className="space-y-6">
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.privacyModal.section1Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.privacyModal.section1Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.privacyModal.section2Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.privacyModal.section2Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.privacyModal.section3Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.privacyModal.section3Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.privacyModal.section4Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.privacyModal.section4Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.privacyModal.section5Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.privacyModal.section5Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.privacyModal.section6Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.privacyModal.section6Text}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Política de Cookies Modal
const CookiesModal = () => {
  const { activeModal, closeModal } = useLegalModal();
  const { t } = useLanguage();
  
  if (activeModal !== 'cookies') return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-[#003399] text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {t.cookiesModal.title}
          </h2>
          <button 
            onClick={closeModal}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(85vh-100px)]">
          <div className="space-y-6">
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.cookiesModal.section1Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.cookiesModal.section1Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.cookiesModal.section2Title}</h3>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-800">{t.cookiesModal.technicalCookies}</h4>
                  <p className="text-gray-600 text-sm">{t.cookiesModal.technicalCookiesText}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-800">{t.cookiesModal.analyticsCookies}</h4>
                  <p className="text-gray-600 text-sm">{t.cookiesModal.analyticsCookiesText}</p>
                </div>
              </div>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.cookiesModal.section3Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.cookiesModal.section3Text}
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-bold text-[#003399] mb-3">{t.cookiesModal.section4Title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.cookiesModal.section4Text}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Legal Pages
const LegalPage = ({ title, children }) => {
  const { t } = useLanguage();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={true} />
      
      <div className="pt-24 pb-16 bg-[#003399]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8">
            <ChevronLeft size={20} />
            {t.nav.home}
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {title}
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <div className="prose prose-lg max-w-none">
          {children}
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

// Team Page
const TeamPage = () => {
  const { t, language } = useLanguage();
  const { openModal } = useLegalModal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const teamMembers = [
    {
      name: t.teamPage?.member1Name || "Jaime Capell",
      role: t.teamPage?.member1Role || "Responsable de Ventas y Proyectos",
      bio: t.teamPage?.member1Bio || "Ingeniero Mecánico y experto en estructuras e instalaciones industriales. Es también Capitán de Yate, un apasionado del mar y de los barcos. Lleva más de 40 años trabajando en la venta de proyectos y productos técnicos. Después de conocer el lanzamiento de Seakeeper en EE.UU., se fue a vivir a Mallorca y compró un barco en 2018 - un Beneteau Antares 9 – en el que montó uno de los primeros SK2, siendo el barco más pequeño de Europa con Seakeeper; posteriormente se unió al equipo del distribuidor, como responsable de ventas y proyectos, para ayudar a la consolidación de la empresa y a la expansión de Seakeeper, Dockmate y Opacmare en las Islas Baleares y el resto de España. En 2021 compró un Beneteau Swift Trawler 41 Fly – el actual barco demo de NextWave Marine – y lo equipó con toda la tecnología que la empresa recomienda a sus clientes (estabilizador Seakeeper 6, mando de control sin cables Dockmate, banco de Litio, potabilizadora, generador, etc) Aporta a NextWave Marine su experiencia, conocimiento técnico, capacidad de comunicación y entusiasmo contagioso.",
      image: "https://customer-assets.emergentagent.com/job_netx-launch/artifacts/9mmia5cc_jaime.jpg"
    },
    {
      name: t.teamPage?.member2Name || "Miriam Cirella",
      role: t.teamPage?.member2Role || "Coordinadora de Operaciones y Administración",
      bio: t.teamPage?.member2Bio || "Miriam lleva muchos años navegando...",
      image: "https://customer-assets.emergentagent.com/job_netx-launch/artifacts/g1lya1ef_miriam.jpg"
    },
    {
      name: t.teamPage?.member3Name || "Markus Ruesseler",
      role: t.teamPage?.member3Role || "Responsable Técnico",
      bio: t.teamPage?.member3Bio || "Especialista en Sistemas Náuticos de Alta Gama. Con años de experiencia en el sector náutico, Markus Ruesseler es un técnico altamente cualificado especializado en la instalación y mantenimiento de sistemas avanzados para embarcaciones. Su trabajo se centra principalmente en soluciones de alto rendimiento como estabilizadores giroscópicos Seakeeper, mandos de control sin cables Dockmate y equipos premium de Opacmare. La pasión por la náutica y la precisión técnica definen cada uno de sus proyectos. Markus destaca por ofrecer instalaciones limpias, seguras y perfectamente integradas, siempre adaptadas a las exigencias específicas de cada cliente y embarcación. Cuenta con el título de Maestro de Seakeeper, la certificación más avanzada que avala su profundo conocimiento y experiencia en uno de los sistemas más innovadores del sector. Como técnico, Markus combina experiencia práctica, compromiso y una constante búsqueda de la excelencia, posicionándose como un profesional de la máxima confianza dentro de la industria náutica.",
      image: "https://customer-assets.emergentagent.com/job_netx-launch/artifacts/cawxlqna_marcus.jpg"
    }
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <InternalPageHeader />
      <SEOHead language={language} />

      {/* Main Content */}
      <main className="flex-grow bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Title */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {t.teamPage?.title || "NUESTRO"} <span className="text-[#003399]">{t.teamPage?.titleHighlight || "EQUIPO"}</span>
            </h1>
          </div>

          {/* Team Members - Horizontal Cards 2:1 ratio */}
          <div className="space-y-6 max-w-4xl mx-auto">
            {teamMembers.map((member, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col md:flex-row md:items-center"
              >
                {/* Image - Left side (smaller, centered) */}
                <div className="md:w-1/4 flex-shrink-0 flex items-center justify-center p-4">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-xl"
                  />
                </div>
                
                {/* Text - Right side (wider) */}
                <div className="md:w-3/4 p-4 md:p-6 flex flex-col justify-center">
                  <h3 className="text-xl font-bold text-[#003399] mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                    {member.name}
                  </h3>
                  <p className="text-[#15BABD] font-semibold text-sm mb-3">{member.role}</p>
                  <p className="text-gray-600 leading-relaxed text-sm">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#003399] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-blue-200 mb-4">© {new Date().getFullYear()} Nextwave Marine. {t.footer?.rights}</p>
          <div className="flex justify-center gap-6">
            <button onClick={() => openModal('avisoLegal')} className="text-blue-200 hover:text-white transition-colors">{t.footer?.legal}</button>
            <button onClick={() => openModal('privacidad')} className="text-blue-200 hover:text-white transition-colors">{t.footer?.privacy}</button>
            <button onClick={() => openModal('cookies')} className="text-blue-200 hover:text-white transition-colors">{t.footer?.cookies}</button>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
};

// Otros Servicios Page (placeholder)
// Internal Page Header with Language Selector
const InternalPageHeader = () => {
  const { t, language, setLanguage } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);
  
  const languages = [
    { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w20/es.png' },
    { code: 'en', name: 'English', flag: 'https://flagcdn.com/w20/gb.png' },
    { code: 'de', name: 'Deutsch', flag: 'https://flagcdn.com/w20/de.png' },
    { code: 'it', name: 'Italiano', flag: 'https://flagcdn.com/w20/it.png' }
  ];
  
  const currentLang = languages.find(l => l.code === language) || languages[0];
  
  return (
    <header className="bg-white shadow-sm py-4 px-6 md:px-12">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <Link to="/">
          <img 
            src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/c529ko5o_Next_Wave_Marine_logo_final.png" 
            alt="Nextwave Marine" 
            className="h-12 object-contain"
          />
        </Link>
        <div className="flex items-center gap-4">
          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <img src={currentLang.flag} alt={currentLang.name} className="w-5 h-4 object-cover" />
              <span className="text-sm font-medium text-gray-700">{language.toUpperCase()}</span>
              <ChevronDown size={16} className="text-gray-500" />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                    className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 ${language === lang.code ? 'bg-blue-50' : ''}`}
                  >
                    <img src={lang.flag} alt={lang.name} className="w-5 h-4 object-cover" />
                    <span className="text-sm">{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link 
            to="/" 
            className="text-[#003399] hover:text-[#15BABD] font-medium flex items-center gap-2"
          >
            <Home size={20} />
            {t.seaTrial?.backHome || "Volver al inicio"}
          </Link>
        </div>
      </div>
    </header>
  );
};

const OtrosServiciosPage = () => {
  const { t, language } = useLanguage();
  const { openModal } = useLegalModal();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeAction, setCodeAction] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([null, null, null]);

  const ADMIN_CODE = "6792";

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload-image-optimized`, formData);
      setSelectedImages(prev => {
        const updated = [...prev];
        updated[index] = response.data.url;
        return updated;
      });
    } catch (error) {
      alert('Error al subir la imagen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const name = form.serviceName.value;
    const description = form.serviceDesc.value;
    const images = selectedImages.filter(img => img !== null);
    
    if (!name || !description) return;

    try {
      await axios.post(`${API}/services`, { name, description, images });
      setShowAddModal(false);
      setSelectedImages([null, null, null]);
      fetchServices();
    } catch (error) {
      alert('Error al crear el servicio');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/services/${id}`);
      fetchServices();
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleCodeSubmit = (code) => {
    if (code === ADMIN_CODE) {
      setShowCodeModal(false);
      if (codeAction === 'add') {
        setShowAddModal(true);
      } else if (codeAction === 'delete' && pendingDeleteId) {
        handleDelete(pendingDeleteId);
        setPendingDeleteId(null);
      }
    } else {
      alert('Código incorrecto');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <InternalPageHeader />
      <SEOHead language={language} />

      <main className="flex-grow bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {t.homepage?.otherServices || "OTROS"} <span className="text-[#003399]">{t.homepage?.otherServicesHighlight || "SERVICIOS"}</span>
            </h1>
            <p className="text-gray-600 text-lg">{t.otherBrands?.servicesSubtitle || "Productos y servicios complementarios para tu embarcación"}</p>
          </div>

          <div className="flex justify-end mb-8">
            <button
              onClick={() => { setCodeAction('add'); setShowCodeModal(true); }}
              data-testid="add-service-btn"
              className="flex items-center gap-2 px-6 py-3 bg-[#003399] text-white font-semibold rounded-lg hover:bg-[#002266] transition-colors"
            >
              <Plus size={20} /> {t.otherBrands?.addService || "Añadir Servicio"}
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><div className="loading-spinner" /></div>
          ) : services.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <Package size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">{t.otherBrands?.noServices || "No hay servicios disponibles aún"}</p>
            </div>
          ) : (
            <div className="space-y-8">
              {services.map((service) => (
                <div key={service.id} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col md:flex-row">
                  {/* Photos left */}
                  <div className="md:w-2/5 p-4">
                    {service.images && service.images.length > 0 ? (
                      <div className="grid gap-2" style={{ gridTemplateRows: service.images.length === 1 ? '1fr' : service.images.length === 2 ? '1fr 1fr' : '1fr 1fr' }}>
                        <img src={service.images[0]} alt={service.name} className="w-full h-48 object-cover rounded-xl" />
                        {service.images.length > 1 && (
                          <div className={`grid gap-2 ${service.images.length === 3 ? 'grid-cols-2' : ''}`}>
                            {service.images.slice(1).map((img, i) => (
                              <img key={i} src={img} alt={`${service.name} ${i + 2}`} className="w-full h-32 object-cover rounded-xl" />
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-xl flex items-center justify-center">
                        <Image size={40} className="text-gray-300" />
                      </div>
                    )}
                  </div>
                  {/* Info right */}
                  <div className="md:w-3/5 p-6 flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-[#003399] mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                        {service.name}
                      </h3>
                      <p className="text-gray-600 whitespace-pre-line">{service.description}</p>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => { setPendingDeleteId(service.id); setCodeAction('delete'); setShowCodeModal(true); }}
                        className="text-red-400 hover:text-red-600 transition-colors p-2"
                        data-testid={`delete-service-${service.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Code Modal */}
      {showCodeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCodeModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-[#003399] mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.otherBrands?.accessCode || "Código de acceso"}</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleCodeSubmit(e.target.code.value); }}>
              <input name="code" type="password" maxLength={4} placeholder={t.otherBrands?.enterCode || "Introduce el código"} className="w-full p-3 border border-gray-300 rounded-lg mb-4 text-center text-2xl tracking-widest" autoFocus />
              <button type="submit" className="w-full py-3 bg-[#003399] text-white font-bold rounded-lg hover:bg-[#002266]">{t.otherBrands?.verify || "Verificar"}</button>
            </form>
          </div>
        </div>
      )}

      {/* Add Service Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full my-8" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-[#003399]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.otherBrands?.newService || "Nuevo Servicio"}</h3>
              <button onClick={() => setShowAddModal(false)}><X size={24} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col md:flex-row gap-6">
                {/* Photos left */}
                <div className="md:w-2/5 space-y-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">{t.otherBrands?.photos || "Fotos (max 3)"}</p>
                  {[0, 1, 2].map((idx) => (
                    <div key={idx} className="relative">
                      {selectedImages[idx] ? (
                        <div className="relative">
                          <img src={selectedImages[idx]} alt={`Foto ${idx + 1}`} className="w-full h-28 object-cover rounded-lg" />
                          <button type="button" onClick={() => setSelectedImages(prev => { const u = [...prev]; u[idx] = null; return u; })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><X size={14} /></button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#15BABD] transition-colors">
                          <div className="text-center">
                            <Plus size={24} className="mx-auto text-gray-400" />
                            <span className="text-xs text-gray-400">{t.otherBrands?.photo || "Foto"} {idx + 1}</span>
                          </div>
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(idx, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
                {/* Info right */}
                <div className="md:w-3/5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.otherBrands?.productName || "Nombre del producto"}</label>
                    <input name="serviceName" type="text" required className="w-full p-3 border border-gray-300 rounded-lg" placeholder={t.otherBrands?.productNamePlaceholder || "Ej: Aire Acondicionado Dometic"} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.otherBrands?.descLabel || "Descripción"}</label>
                    <textarea name="serviceDesc" required rows={6} className="w-full p-3 border border-gray-300 rounded-lg resize-none" placeholder={t.otherBrands?.descPlaceholder || "Descripción del producto o servicio..."} />
                  </div>
                  <button type="submit" className="w-full py-3 bg-[#003399] text-white font-bold rounded-lg hover:bg-[#002266] transition-colors">{t.otherBrands?.createService || "Crear Servicio"}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <footer className="bg-[#003399] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-blue-200 mb-4">© {new Date().getFullYear()} Nextwave Marine. {t.footer?.rights}</p>
          <div className="flex justify-center gap-6">
            <button onClick={() => openModal('avisoLegal')} className="text-blue-200 hover:text-white transition-colors">{t.footer?.legal}</button>
            <button onClick={() => openModal('privacidad')} className="text-blue-200 hover:text-white transition-colors">{t.footer?.privacy}</button>
            <button onClick={() => openModal('cookies')} className="text-blue-200 hover:text-white transition-colors">{t.footer?.cookies}</button>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
};

// Sea Trial Request Page
const PruebaMarPage = () => {
  const { t, language } = useLanguage();
  const { openModal } = useLegalModal();
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    boat: '',
    message: '' 
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        message: `[SOLICITUD PRUEBA DE MAR]\nEmbarcación: ${formData.boat}\n\n${formData.message}`
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', boat: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead language={language} />
      {/* Header */}
      <header className="bg-white shadow-sm py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/c529ko5o_Next_Wave_Marine_logo_final.png" 
              alt="Nextwave Marine" 
              className="h-14"
            />
          </Link>
          <Link 
            to="/" 
            className="text-[#003399] hover:text-[#15BABD] font-medium flex items-center gap-2"
          >
            <Home size={20} />
            {t.seaTrial.backHome}
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {t.seaTrial.title} <span className="text-[#003399]">{t.seaTrial.titleHighlight}</span>
            </h1>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Form - Left Side */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl">
              <form onSubmit={handleSubmit}>
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.seaTrial.formName}</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="form-input-marine"
                      placeholder={t.seaTrial.formNamePlaceholder}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.seaTrial.formEmail}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="form-input-marine"
                      placeholder={t.seaTrial.formEmailPlaceholder}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.seaTrial.formPhone}</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="form-input-marine"
                      placeholder="+34 600 000 000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.seaTrial.formBoat}</label>
                    <input
                      type="text"
                      value={formData.boat}
                      onChange={(e) => setFormData({...formData, boat: e.target.value})}
                      className="form-input-marine"
                      placeholder={t.seaTrial.formBoatPlaceholder}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.seaTrial.formMessage}</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={3}
                      className="form-input-marine resize-none"
                      placeholder={t.seaTrial.formMessagePlaceholder}
                    />
                  </div>

                  <button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="btn-primary-marine w-full flex items-center justify-center gap-2 py-3"
                  >
                    {isSubmitting ? (
                      <div className="loading-spinner" />
                    ) : (
                      <>
                        <Send size={20} /> 
                        {t.seaTrial.submitButton}
                      </>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <p className="text-green-700 font-medium text-sm">{t.seaTrial.successMessage}</p>
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <p className="text-red-700 text-sm">{t.seaTrial.errorMessage}</p>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Info - Right Side */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl">
              <h2 className="text-2xl font-bold text-[#003399] mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {t.seaTrial.infoTitle}
              </h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                {t.seaTrial.infoDesc1}
              </p>
              <p className="text-gray-600 mb-4 leading-relaxed">
                {t.seaTrial.infoDesc2}
              </p>
              
              <h3 className="text-xl font-bold text-[#003399] mb-3 mt-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {t.seaTrial.logisticsTitle}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t.seaTrial.logisticsDesc}
              </p>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-3 text-gray-600">
                  <MapPin className="text-[#15BABD]" size={20} />
                  <span>{t.seaTrial.location}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600 mt-3">
                  <Phone className="text-[#15BABD]" size={20} />
                  <a href="tel:+34600956792" className="hover:text-[#003399]">+34 600 95 67 92</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative text-white py-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/1426718/pexels-photo-1426718.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
          }}
        />
        <div className="absolute inset-0 bg-[#001a4d]/90" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/jl3s23q0_Next_Wave_Marine_con%20blanco_logo_final.png" 
                  alt="Nextwave Marine" 
                  className="h-16"
                />
              </div>
              <p className="text-blue-200 mb-4">{t.footer?.description || "Tu socio de confianza en productos y servicios náuticos."}</p>
              <p className="text-[#15BABD] font-medium italic">{t.footer?.slogan || "Tu pasión por el mar, nuestra especialidad."}</p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.footer?.services?.toUpperCase() || "SERVICIOS"}</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-blue-200 hover:text-white transition-colors">{t.nav?.home || "Inicio"}</Link></li>
                <li><Link to="/#about" className="text-blue-200 hover:text-white transition-colors">{t.nav?.services || "Qué Hacemos"}</Link></li>
                <li><Link to="/#services" className="text-blue-200 hover:text-white transition-colors">{t.nav?.about || "Quiénes Somos"}</Link></li>
                <li><Link to="/trabajos" className="text-blue-200 hover:text-white transition-colors">{t.nav?.works || "Trabajos"}</Link></li>
                <li><Link to="/#contact" className="text-blue-200 hover:text-white transition-colors">{t.nav?.contact || "Contacto"}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.footer.company.toUpperCase()}</h4>
              <ul className="space-y-2">
                <li><button onClick={() => openModal('avisoLegal')} className="text-blue-200 hover:text-white transition-colors">{t.footer.legal}</button></li>
                <li><button onClick={() => openModal('privacidad')} className="text-blue-200 hover:text-white transition-colors">{t.footer.privacy}</button></li>
                <li><button onClick={() => openModal('cookies')} className="text-blue-200 hover:text-white transition-colors">{t.footer.cookies}</button></li>
                <li className="flex items-start gap-2 mt-2">
                  <MapPin size={16} className="text-[#15BABD] mt-0.5 flex-shrink-0" />
                  <span className="text-blue-200 text-sm">C/ Francesc Vallduvi, 18<br />07011 Pol. Can Valero<br />Palma - Mallorca<br />Baleares, España</span>
                </li>
              </ul>
              <div className="flex gap-4 mt-6">
                <a href="https://instagram.com/nextwavemarine" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#15BABD] transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="https://facebook.com/nextwavemarine" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#15BABD] transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="https://youtube.com/@nextwavemarine" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#15BABD] transition-colors">
                  <Youtube size={20} />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t.footer?.contact?.toUpperCase() || "CONTACTO"}</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-[#15BABD]" />
                  <a href="tel:+34600956792" className="text-blue-200 hover:text-white">+34 600 95 67 92</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={18} className="text-[#15BABD]" />
                  <a href="mailto:info@nextwave-marine.com" className="text-blue-200 hover:text-white">info@nextwave-marine.com</a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#15BABD] mt-0.5 flex-shrink-0" />
                  <span className="text-blue-200 text-sm">C/ Francesc Vallduvi, 18<br />07011 Pol. Can Valero<br />Palma - Mallorca<br />Baleares, España</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-blue-400/30 pt-8 text-center">
            <p className="text-blue-200 text-sm">© {new Date().getFullYear()} Nextwave Marine. Todos los derechos reservados.</p>
            <p className="text-blue-300/70 text-xs mt-2">
              Web desarrollada por{' '}
              <a 
                href="https://www.pxs.es" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#15BABD] hover:text-white transition-colors"
              >
                PXS Comunicación
              </a>
            </p>
          </div>
        </div>
      </footer>
      
      <WhatsAppButton />
    </div>
  );
};

const AvisoLegalPage = () => (
  <LegalPage title="AVISO LEGAL">
    <h2 className="text-xl font-bold mt-8 mb-4">1. Datos de Identificación</h2>
    <p className="text-gray-600 mb-4">
      Mextwave Marine - Empresa dedicada a productos y servicios náuticos.
    </p>
    <ul className="list-disc pl-6 text-gray-600 mb-4">
      <li>Dirección: Puerto de Mallorca, Baleares, España</li>
      <li>Teléfono: +34 600 95 67 92</li>
      <li>Email: info@nextwave-marine.com</li>
    </ul>
    <h2 className="text-xl font-bold mt-8 mb-4">2. Propiedad Intelectual</h2>
    <p className="text-gray-600 mb-4">
      Todos los derechos de propiedad intelectual sobre los contenidos de este sitio web están reservados.
    </p>
  </LegalPage>
);

const PoliticaPrivacidadPage = () => (
  <LegalPage title="POLÍTICA DE PRIVACIDAD">
    <h2 className="text-xl font-bold mt-8 mb-4">1. Responsable del Tratamiento</h2>
    <p className="text-gray-600 mb-4">
      Mextwave Marine es responsable del tratamiento de los datos personales proporcionados.
    </p>
    <h2 className="text-xl font-bold mt-8 mb-4">2. Finalidad del Tratamiento</h2>
    <p className="text-gray-600 mb-4">
      Los datos se utilizan para gestionar consultas y proporcionar información sobre nuestros servicios.
    </p>
  </LegalPage>
);

const PoliticaCookiesPage = () => (
  <LegalPage title="POLÍTICA DE COOKIES">
    <h2 className="text-xl font-bold mt-8 mb-4">1. ¿Qué son las Cookies?</h2>
    <p className="text-gray-600 mb-4">
      Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo.
    </p>
    <h2 className="text-xl font-bold mt-8 mb-4">2. Tipos de Cookies</h2>
    <p className="text-gray-600 mb-4">
      Utilizamos cookies técnicas y analíticas para mejorar tu experiencia.
    </p>
  </LegalPage>
);

// Seakeeper Products Page
const SeakeeperPage = () => {
  const { t, language, setLanguage } = useLanguage();
  const { openModal } = useLegalModal();
  const [langOpen, setLangOpen] = useState(false);
  
  const languages = [
    { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w20/es.png' },
    { code: 'en', name: 'English', flag: 'https://flagcdn.com/w20/gb.png' },
    { code: 'de', name: 'Deutsch', flag: 'https://flagcdn.com/w20/de.png' },
    { code: 'it', name: 'Italiano', flag: 'https://flagcdn.com/w20/it.png' }
  ];
  
  const currentLang = languages.find(l => l.code === language) || languages[0];
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const ventajas = t.seakeeperPage?.advantages || [
    { title: "Libertad de fondeo", desc: "No dependes de calas protegidas; puedes tirar el ancla donde más te guste sin que el oleaje arruine el momento." },
    { title: "Adiós al mareo", desc: "Eliminas el miedo de los invitados propensos a marearse, permitiendo que todos se relajen y disfruten de la jornada." },
    { title: "Efectividad total", desc: "A diferencia de las aletas, funciona al 100% incluso cuando el barco está parado o navegando a baja velocidad." },
    { title: "Navegación sin límites", desc: "Amplías tus días de salida al mar; condiciones que antes te dejarían en puerto ahora son perfectamente navegables." },
    { title: "Seguridad en el traspaso", desc: "Facilitas el acceso desde la auxiliar (tender) al evitar que el barco principal oscile bruscamente." },
    { title: "Plataforma de pesca estable", desc: "Fundamental para el curricán o pesca de fondo, permitiendo que los pescadores se concentren en la captura y no en mantener el equilibrio." },
    { title: "Menos fatiga física", desc: "Evitas el cansancio extremo que produce el esfuerzo constante de los músculos para compensar el balanceo del barco." },
    { title: "Maniobras de carga seguras", desc: "Iza o bota tu moto de agua o embarcación auxiliar con total estabilidad, reduciendo riesgos de golpes o accidentes." },
    { title: "Inversión rentable", desc: "Un barco con Seakeeper tiene un valor de reventa mucho más alto y se vende en mucho menos tiempo que uno que no lo tiene." },
    { title: "Confort en puerto", desc: "Incluso amarrado, neutraliza el molesto balanceo provocado por las estelas de otros barcos o puertos expuestos." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead language={language} />
      {/* Language Selector - Fixed top right */}
      <div className="fixed top-4 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-white/90 backdrop-blur rounded-lg shadow-lg hover:bg-white transition-colors"
          >
            <img src={currentLang.flag} alt={currentLang.name} className="w-5 h-4 object-cover" />
            <span className="text-sm font-medium text-gray-700">{language.toUpperCase()}</span>
            <ChevronDown size={16} className="text-gray-500" />
          </button>
          {langOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                  className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 ${language === lang.code ? 'bg-blue-50' : ''}`}
                >
                  <img src={lang.flag} alt={lang.name} className="w-5 h-4 object-cover" />
                  <span className="text-sm">{lang.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img 
          src="https://images.pexels.com/photos/163236/luxury-yacht-boat-speed-water-163236.jpeg?auto=compress&cs=tinysrgb&w=1920" 
          alt="Seakeeper" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#003399]/80 to-transparent" />
        
        {/* Volver al inicio - izquierda abajo */}
        <div className="absolute bottom-6 left-6 md:left-12 z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ChevronLeft size={20} />
            {t.seaTrial?.backHome || "Back"}
          </Link>
        </div>
        
        {/* Logo y botón Equipos - centro abajo */}
        <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            SEAKEEPER
          </h1>
          <p className="text-white/90 text-sm md:text-base mb-4">{t.seakeeperPage?.subtitle || "Estabilizador giroscópico"}</p>
          <a 
            href="https://www.seakeeper.com/products/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-6 py-2 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-[#003399] transition-colors duration-300"
          >
            {t.seakeeperPage?.visitButton || "Visitar Seakeeper"}
          </a>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center mb-12">
          <div className="md:w-1/2 text-left">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {t.seakeeperPage?.advantagesTitle || "VENTAJAS DE"}<br /><span className="text-[#003399]">SEAKEEPER</span>
            </h2>
            <p className="text-gray-600">
              {t.seakeeperPage?.advantagesDesc || "Descubre por qué Seakeeper es la mejor inversión para tu embarcación"}
            </p>
          </div>
          <div className="md:w-1/4 w-48 rounded-2xl overflow-hidden shadow-lg flex-shrink-0">
            <video 
              autoPlay 
              muted 
              loop 
              playsInline
              className="w-full h-auto max-h-[280px] object-cover"
            >
              <source src="/seakeeper_video.mp4" type="video/mp4" />
            </video>
          </div>
        </div>

        <div className="space-y-6">
          {ventajas.map((ventaja, index) => (
            <div key={index} className="flex gap-6 items-start bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex-shrink-0 w-12 h-12 bg-[#003399] text-white rounded-full flex items-center justify-center font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {index + 1}
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#003399] mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  {ventaja.title}
                </h3>
                <p className="text-gray-600">{ventaja.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link 
            to="/prueba-de-mar" 
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#15BABD] text-white font-semibold rounded-lg hover:bg-[#003399] transition-colors"
          >
            {t.seakeeperPage?.requestSeaTrial || "Solicitar Prueba de Mar"}
            <ChevronRight size={20} />
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative text-white py-12 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.pexels.com/photos/1426718/pexels-photo-1426718.jpeg?auto=compress&cs=tinysrgb&w=1920')` }}
        />
        <div className="absolute inset-0 bg-[#001a4d]/90" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/jl3s23q0_Next_Wave_Marine_con%20blanco_logo_final.png" 
            alt="Nextwave Marine" 
            className="h-12 mx-auto mb-4"
          />
          <p className="text-blue-200 mb-4">© {new Date().getFullYear()} Nextwave Marine. {t.footer.rights}</p>
          <div className="flex justify-center gap-6">
            <button onClick={() => openModal('avisoLegal')} className="text-blue-200 hover:text-white transition-colors">{t.footer.legal}</button>
            <button onClick={() => openModal('privacidad')} className="text-blue-200 hover:text-white transition-colors">{t.footer.privacy}</button>
            <button onClick={() => openModal('cookies')} className="text-blue-200 hover:text-white transition-colors">{t.footer.cookies}</button>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
};

// VCard Page for Jaime
const JaimeVCardPage = () => {
  const downloadVCard = () => {
    const vCardData = `BEGIN:VCARD
VERSION:3.0
N:Capell;Jaime;;;
FN:Jaime Capell
ORG:NextWave Marine
TITLE:Sales & Project Manager
TEL;TYPE=CELL:+34 600 95 67 92
TEL;TYPE=WORK:+34 625 03 42 34
EMAIL:jaime@nextwave-marine.com
ADR;TYPE=WORK:;;C/ Francesc Vallduvi 18, Pol. Can Valero;Palma de Mallorca;Baleares;07011;Spain
URL:https://www.nextwave-marine.com
NOTE:NextWave Marine - Your passion for the sea, our expertise
END:VCARD`;

    const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Jaime_Capell_NextWave_Marine.vcf');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#003399] to-[#001a4d] flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="mb-8">
        <img 
          src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/uwz2cpai_Next_Wave_Marine_con%20blanco_logo_final.png" 
          alt="NextWave Marine"
          className="h-24 md:h-28 rounded-xl"
        />
      </div>

      {/* Card */}
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full">
        {/* Profile */}
        <div className="text-center mb-6">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-[#15BABD]">
            <img 
              src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/9mmia5cc_jaime.jpg" 
              alt="Jaime Capell"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-bold text-[#003399]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Jaime Capell
          </h1>
          <p className="text-[#15BABD] font-medium">Sales & Project Manager</p>
        </div>

        {/* Contact Info */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <MapPin className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Location</p>
              <p className="text-gray-800 font-medium text-sm">C/ Francesc Vallduvi, 18 - Palma, Mallorca</p>
            </div>
          </div>

          <a href="tel:+34600956792" className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-[#003399]/5 transition-colors">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <Phone className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-gray-800 font-medium text-sm">+34 600 95 67 92</p>
            </div>
          </a>

          <a href="mailto:jaime@nextwave-marine.com" className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-[#003399]/5 transition-colors">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <Mail className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-gray-800 font-medium text-sm">jaime@nextwave-marine.com</p>
            </div>
          </a>
        </div>

        {/* Save Button */}
        <button
          onClick={downloadVCard}
          className="w-full py-4 bg-[#003399] text-white font-bold rounded-xl hover:bg-[#002266] transition-colors flex items-center justify-center gap-2"
          style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}
        >
          <Download size={20} />
          SAVE CONTACT
        </button>
      </div>

      {/* Footer */}
      <p className="text-white/40 text-xs mt-8">nextwave-marine.com</p>
    </div>
  );
};

// Miriam VCard Page
const MiriamVCardPage = () => {
  const downloadVCard = () => {
    const vCardData = `BEGIN:VCARD
VERSION:3.0
N:Cirella;Miriam;;;
FN:Miriam Cirella
ORG:NextWave Marine
TITLE:Operations & Administration Coordinator
TEL;TYPE=CELL:+34 605 77 95 46
TEL;TYPE=WORK:+34 625 03 42 34
EMAIL:miriam@nextwave-marine.com
ADR;TYPE=WORK:;;C/ Francesc Vallduvi 18, Pol. Can Valero;Palma de Mallorca;Baleares;07011;Spain
URL:https://www.nextwave-marine.com
NOTE:NextWave Marine - Your passion for the sea, our expertise
END:VCARD`;

    const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Miriam_Cirella_NextWave_Marine.vcf');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#003399] to-[#001a4d] flex flex-col items-center justify-center p-6">
      <div className="mb-8">
        <img 
          src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/uwz2cpai_Next_Wave_Marine_con%20blanco_logo_final.png" 
          alt="NextWave Marine"
          className="h-24 md:h-28 rounded-xl"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full">
        <div className="text-center mb-6">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-[#15BABD]">
            <img 
              src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/g1lya1ef_miriam.jpg" 
              alt="Miriam Cirella"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-bold text-[#003399]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Miriam Cirella
          </h1>
          <p className="text-[#15BABD] font-medium">Operations & Administration</p>
        </div>

        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <MapPin className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Location</p>
              <p className="text-gray-800 font-medium text-sm">C/ Francesc Vallduvi, 18 - Palma, Mallorca</p>
            </div>
          </div>

          <a href="tel:+34605779546" className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-[#003399]/5 transition-colors">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <Phone className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-gray-800 font-medium text-sm">+34 605 77 95 46</p>
            </div>
          </a>

          <a href="tel:+34625034234" className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-[#003399]/5 transition-colors">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <Phone className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Office</p>
              <p className="text-gray-800 font-medium text-sm">+34 625 03 42 34</p>
            </div>
          </a>

          <a href="mailto:miriam@nextwave-marine.com" className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-[#003399]/5 transition-colors">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <Mail className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-gray-800 font-medium text-sm">miriam@nextwave-marine.com</p>
            </div>
          </a>
        </div>

        <button
          onClick={downloadVCard}
          data-testid="miriam-save-contact-btn"
          className="w-full py-4 bg-[#003399] text-white font-bold rounded-xl hover:bg-[#002266] transition-colors flex items-center justify-center gap-2"
          style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}
        >
          <Download size={20} />
          SAVE CONTACT
        </button>
      </div>

      <p className="text-white/40 text-xs mt-8">nextwave-marine.com</p>
    </div>
  );
};

// Markus VCard Page
const MarkusVCardPage = () => {
  const downloadVCard = () => {
    const vCardData = `BEGIN:VCARD
VERSION:3.0
N:Ruesseler;Markus;;;
FN:Markus Ruesseler
ORG:NextWave Marine
TITLE:Technical Manager
TEL;TYPE=CELL:+34 652 53 14 80
TEL;TYPE=WORK:+34 625 03 42 34
EMAIL:markus@nextwave-marine.com
ADR;TYPE=WORK:;;C/ Francesc Vallduvi 18, Pol. Can Valero;Palma de Mallorca;Baleares;07011;Spain
URL:https://www.nextwave-marine.com
NOTE:NextWave Marine - Your passion for the sea, our expertise
END:VCARD`;

    const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Markus_Ruesseler_NextWave_Marine.vcf');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#003399] to-[#001a4d] flex flex-col items-center justify-center p-6">
      <div className="mb-8">
        <img 
          src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/uwz2cpai_Next_Wave_Marine_con%20blanco_logo_final.png" 
          alt="NextWave Marine"
          className="h-24 md:h-28 rounded-xl"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full">
        <div className="text-center mb-6">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-[#15BABD]">
            <img 
              src="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/cawxlqna_marcus.jpg" 
              alt="Markus Ruesseler"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-bold text-[#003399]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Markus Ruesseler
          </h1>
          <p className="text-[#15BABD] font-medium">Technical Manager</p>
        </div>

        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <MapPin className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Location</p>
              <p className="text-gray-800 font-medium text-sm">C/ Francesc Vallduvi, 18 - Palma, Mallorca</p>
            </div>
          </div>

          <a href="tel:+34652531480" className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-[#003399]/5 transition-colors">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <Phone className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-gray-800 font-medium text-sm">+34 652 53 14 80</p>
            </div>
          </a>

          <a href="tel:+34625034234" className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-[#003399]/5 transition-colors">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <Phone className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Office</p>
              <p className="text-gray-800 font-medium text-sm">+34 625 03 42 34</p>
            </div>
          </a>

          <a href="mailto:markus@nextwave-marine.com" className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-[#003399]/5 transition-colors">
            <div className="w-10 h-10 bg-[#003399]/10 rounded-full flex items-center justify-center">
              <Mail className="text-[#003399]" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-gray-800 font-medium text-sm">markus@nextwave-marine.com</p>
            </div>
          </a>
        </div>

        <button
          onClick={downloadVCard}
          data-testid="markus-save-contact-btn"
          className="w-full py-4 bg-[#003399] text-white font-bold rounded-xl hover:bg-[#002266] transition-colors flex items-center justify-center gap-2"
          style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}
        >
          <Download size={20} />
          SAVE CONTACT
        </button>
      </div>

      <p className="text-white/40 text-xs mt-8">nextwave-marine.com</p>
    </div>
  );
};


// Dockmate Products Page
const DockmatePage = () => {
  const { t, language, setLanguage } = useLanguage();
  const { openModal } = useLegalModal();
  const [langOpen, setLangOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  
  const languages = [
    { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w20/es.png' },
    { code: 'en', name: 'English', flag: 'https://flagcdn.com/w20/gb.png' },
    { code: 'de', name: 'Deutsch', flag: 'https://flagcdn.com/w20/de.png' },
    { code: 'it', name: 'Italiano', flag: 'https://flagcdn.com/w20/it.png' }
  ];
  
  const currentLang = languages.find(l => l.code === language) || languages[0];
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const ventajas = t.dockmatePage?.advantages || [
    { title: "Control total", desc: "Atraca y desatraca tu embarcación desde cualquier punto del barco con el mando inalámbrico." },
    { title: "Maniobras sin estrés", desc: "Elimina la tensión de las maniobras en puerto, especialmente en espacios reducidos o con viento." },
    { title: "Seguridad aumentada", desc: "Evita golpes y roces al poder controlar la embarcación con total precisión desde la proa o la popa." },
    { title: "Navegación en solitario", desc: "Ideal para quienes navegan solos, sin necesidad de ayuda en los atraques." },
    { title: "Compatible con tu barco", desc: "Se adapta a la mayoría de sistemas de propulsión: fuerabordas, intrabordas, cola y pod drives." },
    { title: "Instalación profesional", desc: "Nuestro equipo técnico certificado garantiza una integración perfecta con tu embarcación." },
    { title: "Facilidad de uso", desc: "Interfaz intuitiva que permite controlar tu barco con la máxima precisión desde el primer momento." },
    { title: "Ahorro de tiempo", desc: "Reduce significativamente el tiempo necesario para las maniobras de atraque y desatraque." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead language={language} />
      {/* Language Selector - Fixed top right */}
      <div className="fixed top-4 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-white/90 backdrop-blur rounded-lg shadow-lg hover:bg-white transition-colors"
          >
            <img src={currentLang.flag} alt={currentLang.name} className="w-5 h-4 object-cover" />
            <span className="text-sm font-medium text-gray-700">{language.toUpperCase()}</span>
            <ChevronDown size={16} className="text-gray-500" />
          </button>
          {langOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                  className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 ${language === lang.code ? 'bg-blue-50' : ''}`}
                >
                  <img src={lang.flag} alt={lang.name} className="w-5 h-4 object-cover" />
                  <span className="text-sm">{lang.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img 
          src="https://images.pexels.com/photos/1007836/pexels-photo-1007836.jpeg?auto=compress&cs=tinysrgb&w=1920" 
          alt="Dockmate" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#003399]/80 to-transparent" />
        
        {/* Volver al inicio - izquierda abajo */}
        <div className="absolute bottom-6 left-6 md:left-12 z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ChevronLeft size={20} />
            {t.seaTrial?.backHome || "Back"}
          </Link>
        </div>
        
        {/* Logo y título - centro abajo */}
        <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            DOCKMATE
          </h1>
          <p className="text-white/90 text-sm md:text-base mb-4">{t.dockmatePage?.subtitle || "Control remoto de maniobras"}</p>
          <a 
            href="https://dockmate.eu/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-6 py-2 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-[#003399] transition-colors duration-300"
          >
            {t.dockmatePage?.visitButton || "Visitar Dockmate"}
          </a>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          {t.dockmatePage?.advantagesTitle || "VENTAJAS DE"} <span className="text-[#003399]">DOCKMATE</span>
        </h2>
        <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
          {t.dockmatePage?.advantagesSubtitle || "Descubre por qué Dockmate revoluciona las maniobras de tu embarcación"}
        </p>

        {/* Video thumbnail */}
        <button 
          onClick={() => setVideoOpen(true)}
          className="block w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-12 relative group cursor-pointer"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/q545wa9f_image.png" 
            alt="Dockmate Video - The Boat Show" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
            <span className="text-white text-2xl font-semibold flex items-center gap-3">
              {t.dockmatePage?.watchVideo || "Ver video"}
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="white" stroke="none">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            </span>
          </div>
        </button>

        <VideoModal 
          isOpen={videoOpen} 
          onClose={() => setVideoOpen(false)} 
          videoUrl="https://customer-assets.emergentagent.com/job_netx-launch/artifacts/9muy66om_%5BENG%5D%20Dock%20your%20boat%20in%20seconds%20with%20DOCKMATE%20-%20The%20Boat%20Show.mp4"
          title="Dockmate - The Boat Show"
        />

        <div className="space-y-6">
          {ventajas.map((ventaja, index) => (
            <div key={index} className="flex gap-6 items-start bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex-shrink-0 w-12 h-12 bg-[#003399] text-white rounded-full flex items-center justify-center font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {index + 1}
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#003399] mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  {ventaja.title}
                </h3>
                <p className="text-gray-600">{ventaja.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link 
            to="/#contact" 
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#15BABD] text-white font-semibold rounded-lg hover:bg-[#003399] transition-colors"
          >
            {t.dockmatePage?.contactButton || "Contactar"}
            <ChevronRight size={20} />
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative text-white py-12 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.pexels.com/photos/1426718/pexels-photo-1426718.jpeg?auto=compress&cs=tinysrgb&w=1920')` }}
        />
        <div className="absolute inset-0 bg-[#001a4d]/90" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_bf4ebf2d-e70c-4269-8975-8daa1f36cb23/artifacts/jl3s23q0_Next_Wave_Marine_con%20blanco_logo_final.png" 
            alt="Nextwave Marine" 
            className="h-12 mx-auto mb-4"
          />
          <p className="text-blue-200 mb-4">© {new Date().getFullYear()} Nextwave Marine. {t.footer.rights}</p>
          <div className="flex justify-center gap-6">
            <button onClick={() => openModal('avisoLegal')} className="text-blue-200 hover:text-white transition-colors">{t.footer.legal}</button>
            <button onClick={() => openModal('privacidad')} className="text-blue-200 hover:text-white transition-colors">{t.footer.privacy}</button>
            <button onClick={() => openModal('cookies')} className="text-blue-200 hover:text-white transition-colors">{t.footer.cookies}</button>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
};

// Home Page
const HomePage = () => {
  const { language } = useLanguage();
  const location = useLocation();
  
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <>
      <SEOHead language={language} />
      <Navigation />
      <HeroSection />
      <AboutSection />
      <QuienesSomosSection />
      <WorksSection />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </>
  );
};

// Main App
function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <LegalModalProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/trabajos" element={<WorksPage />} />
                <Route path="/prueba-de-mar" element={<PruebaMarPage />} />
                <Route path="/equipo" element={<TeamPage />} />
                <Route path="/otros-servicios" element={<OtrosServiciosPage />} />
                <Route path="/seakeeper" element={<SeakeeperPage />} />
                <Route path="/dockmate" element={<DockmatePage />} />
                <Route path="/jaime" element={<JaimeVCardPage />} />
                <Route path="/miriam" element={<MiriamVCardPage />} />
                <Route path="/markus" element={<MarkusVCardPage />} />
              </Routes>
              <CookieBanner />
              <AvisoLegalModal />
              <PrivacidadModal />
              <CookiesModal />
            </BrowserRouter>
          </LegalModalProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
    </HelmetProvider>
  );
}

export default App;
